import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Typography, Table, Tag, Button, Space, Select, Modal, Tabs, Form,
  Input, InputNumber, DatePicker, Rate, message, Popconfirm, Statistic, Row, Col, Empty, Upload, Checkbox, Divider, Radio, Progress, Avatar, Alert
} from 'antd';
import { TeamOutlined, EditOutlined, DollarOutlined, CalendarOutlined, StarOutlined, DeleteOutlined, UploadOutlined, PaperClipOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, SaveOutlined, QrcodeOutlined, DownloadOutlined, LoginOutlined, LogoutOutlined, FileExcelOutlined, UserOutlined, FileTextOutlined, IdcardOutlined, FilePdfOutlined, EnvironmentOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../utils/api';
import { useDeepLinkWhen } from '../utils/deeplink';

const { Title, Text } = Typography;
const API_BASE = process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api$/, '') : '';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEARS = [];
{
  const y = new Date().getFullYear();
  for (let i = y - 3; i <= y + 1; i++) YEARS.push(i);
}

export default function StaffManagement({ view = 'all' }) {
  const now = new Date();
  // AI answers open this page on the asked day/month (?date= / ?month=).
  const when = useDeepLinkWhen();
  const [month, setMonth] = useState(when.month ? when.month.month() + 1 : now.getMonth() + 1);
  const [year, setYear] = useState(when.month ? when.month.year() : now.getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [activeUser, setActiveUser] = useState(null);
  const [manageTab, setManageTab] = useState('profile');
  const [salarySearch, setSalarySearch] = useState('');

  const [profileForm] = Form.useForm();
  const [salaryForm] = Form.useForm();
  const [salaryProof, setSalaryProof] = useState(null);
  const [attForm] = Form.useForm();
  const [attRangeForm] = Form.useForm();
  const [perfForm] = Form.useForm();

  // Daily bulk attendance
  const [attDate, setAttDate] = useState(when.date || dayjs());
  const [attDayList, setAttDayList] = useState([]);
  const [attDayLoading, setAttDayLoading] = useState(false);
  const [attMarks, setAttMarks] = useState({});
  const [attSaving, setAttSaving] = useState(false);
  // Per-staff monthly colour grid (day -> status)
  const [gridMarks, setGridMarks] = useState({});
  const [origGridMarks, setOrigGridMarks] = useState({});
  const [gridSaving, setGridSaving] = useState(false);

  // QR Code
  const [qrToken, setQrToken] = useState(null);
  const [qrSheetUrl, setQrSheetUrl] = useState(null);
  const [qrSheetOpen, setQrSheetOpen] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  // Office Location (GPS)
  const [officeLoc, setOfficeLoc] = useState({ office_lat: 0, office_lng: 0, office_radius: 100, login_radius: 500 });
  const [officeLocSaving, setOfficeLocSaving] = useState(false);
  // Attendance Log
  const [attLog, setAttLog] = useState([]);
  const [attLogLoading, setAttLogLoading] = useState(false);
  const [logFilter, setLogFilter] = useState(when.date ? 'custom' : 'today');
  const [summaryMonth, setSummaryMonth] = useState(dayjs());
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [logDateRange, setLogDateRange] = useState(when.date ? [when.date, when.date] : [dayjs(), dayjs()]);
  // Coming here again from another AI answer (different ?date=) must re-filter.
  const whenDateKey = when.date ? when.date.format('YYYY-MM-DD') : '';
  const whenMonthKey = when.month ? when.month.format('YYYY-MM') : '';
  useEffect(() => {
    if (whenDateKey) {
      setAttDate(dayjs(whenDateKey));
      setLogFilter('custom');
      setLogDateRange([dayjs(whenDateKey), dayjs(whenDateKey)]);
    }
    if (whenMonthKey) {
      setMonth(dayjs(whenMonthKey + '-01').month() + 1);
      setYear(dayjs(whenMonthKey + '-01').year());
    }
  }, [whenDateKey, whenMonthKey]);

  // Advances/Loans
  const [advances, setAdvances] = useState([]);
  const [advanceTotal, setAdvanceTotal] = useState(0);
  const [advanceForm] = Form.useForm();
  const [editAdvForm] = Form.useForm();
  const [editingAdv, setEditingAdv] = useState(null);
  // Custom Holidays
  const [holidays, setHolidays] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);
  const [holidayModal, setHolidayModal] = useState(false);
  const [holidayForm] = Form.useForm();

  // Staff-submitted profile change requests (admin approval)
  const [profileReqs, setProfileReqs] = useState([]);
  const [profileReqsLoading, setProfileReqsLoading] = useState(false);
  const [reqFields, setReqFields] = useState({});

  const loadProfileReqs = useCallback(async () => {
    setProfileReqsLoading(true);
    try {
      const res = await api.get('/staff/profile-requests?status=pending');
      const list = Array.isArray(res.data) ? res.data : [];
      setProfileReqs(list);
      const sel = {};
      list.forEach(r => { sel[r.id] = Object.keys(r.changes || {}); });
      setReqFields(sel);
    } catch (e) { setProfileReqs([]); }
    finally { setProfileReqsLoading(false); }
  }, []);

  const reviewProfileReq = async (id, status) => {
    try {
      await api.post('/staff/profile-request-review', { id, status, fields: reqFields[id] || [] });
      message.success(status === 'approved' ? 'Approve ho gaya — details update ho gayi' : 'Request reject kar di');
      loadProfileReqs();
      load();
    } catch (e) { message.error(e.response?.data?.error || 'Failed to review request'); }
  };

  const loadHolidays = useCallback(async () => {
    setHolidaysLoading(true);
    try {
      const res = await api.get(`/staff/holidays?year=${year}`);
      setHolidays(res.data || []);
    } catch (e) {}
    finally { setHolidaysLoading(false); }
  }, [year]);

  const addHoliday = async (values) => {
    try {
      await api.post('/staff/holidays', {
        holiday_date: values.holiday_date.format('YYYY-MM-DD'),
        title: values.title
      });
      message.success('Holiday added! Attendance marked for all staff.');
      setHolidayModal(false);
      holidayForm.resetFields();
      loadHolidays();
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to add holiday');
    }
  };

  const deleteHoliday = async (id) => {
    try {
      await api.delete(`/staff/holidays/${id}`);
      message.success('Holiday deleted');
      loadHolidays();
    } catch (e) { message.error('Failed to delete holiday'); }
  };

  const loadQrToken = useCallback(async () => {
    try {
      const res = await api.get('/staff/qr-today');
      setQrToken(res.data?.token || null);
    } catch (e) {}
  }, []);

  const generateQr = async () => {
    setQrLoading(true);
    try {
      const res = await api.post('/staff/qr-generate');
      setQrToken(res.data.token);
      message.success('QR Code generated!');
    } catch (err) { message.error('Failed to generate QR'); }
    finally { setQrLoading(false); }
  };

  const downloadQr = async () => {
    try {
      await buildAndDownloadQr();
    } catch (e) {
      message.error(`QR download fail: ${e?.message || e}`);
    }
  };

  const buildAndDownloadQr = async () => {
    const qrCanvas = document.querySelector('#office-qr-code canvas');
    if (!qrCanvas) { message.error('QR ready nahi hai — pehle QR generate karo'); return; }
    const logo = await new Promise((resolve) => {
      // window.Image — antd ka Image component isi naam ko dhak deta hai
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = '/logo192.png';
    });
    const scale = 3; // 3x resolution for crisp print quality
    const W = 500, H = 700, qrSize = 280;
    const c = document.createElement('canvas');
    c.width = W * scale; c.height = H * scale;
    const ctx = c.getContext('2d');
    ctx.scale(scale, scale);
    ctx.imageSmoothingEnabled = false;
    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    // Outer border with rounded feel (double border)
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 4;
    ctx.strokeRect(10, 10, W - 20, H - 20);
    ctx.strokeStyle = '#667eea';
    ctx.lineWidth = 2;
    ctx.strokeRect(16, 16, W - 32, H - 32);
    // Top gradient bar
    const grad = ctx.createLinearGradient(0, 0, W, 0);
    grad.addColorStop(0, '#667eea');
    grad.addColorStop(1, '#764ba2');
    ctx.fillStyle = grad;
    ctx.fillRect(16, 16, W - 32, 80);
    // Company name on gradient
    if (logo) {
      ctx.imageSmoothingEnabled = true;
      ctx.drawImage(logo, 26, 24, 64, 64);
      ctx.imageSmoothingEnabled = false;
    }
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 32px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('BAINSLA MUSIC', W / 2, 62);
    ctx.font = '14px Arial, sans-serif';
    ctx.fillText('Staff Attendance System', W / 2, 85);
    // Divider line
    ctx.strokeStyle = '#e8e8e8';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(40, 110); ctx.lineTo(W - 40, 110); ctx.stroke();
    // Title
    ctx.fillStyle = '#1a1a2e';
    ctx.font = 'bold 20px Arial, sans-serif';
    ctx.fillText('SCAN TO MARK ATTENDANCE', W / 2, 140);
    // QR code centered with shadow effect
    const qrX = (W - qrSize) / 2, qrY = 160;
    ctx.shadowColor = 'rgba(0,0,0,0.15)';
    ctx.shadowBlur = 15;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 4;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30);
    ctx.shadowColor = 'transparent';
    // QR border
    ctx.strokeStyle = '#667eea';
    ctx.lineWidth = 3;
    ctx.strokeRect(qrX - 15, qrY - 15, qrSize + 30, qrSize + 30);
    // QR code — pixel-perfect (no smoothing for sharp QR)
    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);
    // Instructions box
    const instrY = qrY + qrSize + 35;
    ctx.fillStyle = '#f0f5ff';
    ctx.fillRect(40, instrY, W - 80, 100);
    ctx.strokeStyle = '#667eea';
    ctx.lineWidth = 1;
    ctx.strokeRect(40, instrY, W - 80, 100);
    ctx.fillStyle = '#333333';
    ctx.font = 'bold 16px Arial, sans-serif';
    ctx.fillText('HOW TO SCAN', W / 2, instrY + 25);
    ctx.font = '13px Arial, sans-serif';
    ctx.fillStyle = '#555555';
    ctx.fillText('1. Open Camera on your phone', W / 2, instrY + 48);
    ctx.fillText('2. Point camera at this QR code', W / 2, instrY + 66);
    ctx.fillText('3. Your attendance will be recorded', W / 2, instrY + 84);
    // Footer with gradient
    const footY = H - 55;
    const grad2 = ctx.createLinearGradient(0, footY, W, footY);
    grad2.addColorStop(0, '#667eea');
    grad2.addColorStop(1, '#764ba2');
    ctx.fillStyle = grad2;
    ctx.fillRect(16, footY, W - 32, 39);
    ctx.fillStyle = '#ffffff';
    ctx.font = '12px Arial, sans-serif';
    ctx.fillText('Bainsla Music Data Dashboard  |  data.intubemedia.com', W / 2, footY + 24);
    // Sheet ban gayi. Blob/dataURL download ko browser aur PWA block kar dete hain, is liye
    // sheet server par bhej kar wahin se asli file download (Content-Disposition) karate hain.
    // Preview modal fallback ke liye khula rehta hai.
    let dataUrl;
    try {
      dataUrl = c.toDataURL('image/png');
    } catch (e) {
      message.error('QR sheet ban nahi payi — dobara try karo');
      return;
    }
    const bin = atob(dataUrl.split(',')[1]);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    setQrSheetUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(new Blob([bytes], { type: 'image/png' })); });
    try {
      await api.post('/staff/qr-sheet', { image: dataUrl });
      window.location.href = `${API_BASE}/api/staff/qr-sheet?token=${encodeURIComponent(localStorage.getItem('token') || '')}`;
      message.success('QR download ho raha hai — Downloads folder me milegi');
    } catch (e) {
      setQrSheetOpen(true);
      message.warning('Server se download nahi hua — neeche preview se PNG save karo');
    }
  };

  const printQrSheet = () => {
    if (!qrSheetUrl) return;
    const w = window.open('', '_blank');
    if (!w) { message.error('Popup block hai — browser me popup allow karo'); return; }
    w.document.write(`<html><head><title>Attendance QR</title></head><body style="margin:0;text-align:center">` +
      `<img src="${qrSheetUrl}" style="max-width:100%" onload="window.focus();window.print()" /></body></html>`);
    w.document.close();
  };

  // Build staff summary from attLog
  const buildSummary = (data) => {
    const map = {};
    data.forEach(r => {
      const name = r.full_name || r.username;
      if (!map[r.user_id]) map[r.user_id] = { user_id: r.user_id, name, present: 0, absent: 0, half_day: 0, leave: 0, holiday: 0, total: 0 };
      map[r.user_id][r.status] = (map[r.user_id][r.status] || 0) + 1;
      map[r.user_id].total++;
    });
    return Object.values(map);
  };

  // Which period the Attendance Log currently shows (filter -> {from,to,label,month,year})
  const logPeriod = (f = logFilter, r = logDateRange) => {
    const t = dayjs();
    if (f === 'today') return { from: t, to: t, label: t.format('DD MMM YYYY') };
    if (f === '7d') return { from: t.subtract(6, 'day'), to: t, label: 'Last 7 days' };
    if (f === '30d') return { from: t.subtract(29, 'day'), to: t, label: 'Last 30 days' };
    if (f === 'custom' && r?.[0] && r?.[1]) return { from: r[0], to: r[1], label: `${r[0].format('DD MMM YYYY')} - ${r[1].format('DD MMM YYYY')}` };
    let m = dayjs().year(year).month(month - 1);
    if (typeof f === 'string' && f.startsWith('m:')) m = dayjs(f.slice(2) + '-01');
    return { from: m.startOf('month'), to: m.endOf('month'), label: m.format('MMMM YYYY'), month: m.month() + 1, year: m.year() };
  };
  const lastMonths = Array.from({ length: 5 }, (_, i) => dayjs().subtract(i + 1, 'month').startOf('month'));

  const statusLabel = s => (s || '').replace('_', ' ').toUpperCase();

  const downloadExcel = () => {
    if (!attLog.length) { message.info('No data to download'); return; }
    const period = logPeriod();
    const staffMap = {};
    const dates = [...new Set(attLog.map(r => r.att_date))].sort();
    attLog.forEach(r => {
      const name = r.full_name || r.username;
      if (!staffMap[r.user_id]) staffMap[r.user_id] = { name, days: {} };
      staffMap[r.user_id].days[r.att_date] = { status: r.status, in: r.check_in_time || '', out: r.check_out_time || '' };
    });
    const aoa = [['Staff Name', ...dates.map(d => dayjs(d).format('DD MMM')), 'Present', 'Half Day', 'Absent']];
    Object.values(staffMap).forEach(s => {
      let present = 0, absent = 0, half = 0;
      const cells = dates.map(d => {
        const day = s.days[d];
        if (!day) return '-';
        if (day.status === 'present') present++;
        if (day.status === 'absent') absent++;
        if (day.status === 'half_day') half++;
        const label = statusLabel(day.status);
        return day.in ? `${label} (${day.in}${day.out ? '-' + day.out : ''})` : label;
      });
      aoa.push([s.name, ...cells, present, half, absent]);
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 22 }, ...dates.map(() => ({ wch: 22 })), { wch: 9 }, { wch: 9 }, { wch: 9 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Daily Grid');
    XLSX.writeFile(wb, `attendance-grid-${period.from.format('YYYY-MM-DD')}_${period.to.format('YYYY-MM-DD')}.xlsx`);
  };

  // Per-staff salary hisab for the selected period (only when it is a whole calendar month)
  const SALARY_HEAD = ['Staff Name', 'Designation', 'Monthly Salary', 'Per Day', 'Present', 'Half Day', 'Absent', 'Pre-Join Days', 'Absent Deduction', 'Half Day Deduction', 'Pre-Join Deduction', 'Total Deduction', 'Net Salary', 'Advance (this month)', 'Carried from Previous Month', 'Net Payable', 'Advance -> Next Month', 'Payment Status', 'Paid Amount', 'Paid Date', 'Mode'];
  const salaryRowArr = r => [r.full_name, r.designation || '', Number(r.monthly_salary || 0), Number(r.per_day_salary || 0), r.present, r.half_day, r.absent, r.pre_join_days || 0,
    Number(r.absent_deduction || 0), Number(r.half_day_deduction || 0), Number(r.pre_join_deduction || 0), Number(r.total_deduction || 0), Number(r.net_salary || 0),
    Number(r.advance_amount || 0), Number(r.advance_carry_in || 0), Number(r.net_payable || 0), Number(r.advance_carry_out || 0), (r.salary_status || 'pending').toUpperCase(), r.paid_amount != null ? Number(r.paid_amount) : '', r.paid_date || '', r.paid_mode || ''];
  const fetchSalaryRows = async (period) => {
    if (!period.month || !period.year) return null;
    try {
      const res = await api.get(`/staff/attendance-monthly?month=${period.month}&year=${period.year}`);
      return res.data.rows || [];
    } catch (e) { message.warning('Salary hisab load nahi hua — sirf attendance export hogi'); return null; }
  };

  // Whole-period attendance as a printable PDF (all staff): staff summary + salary hisab + day-wise log with check-in/out & half-day notes
  const downloadAttendancePDF = async () => {
    if (!attLog.length) { message.info('No data to download'); return; }
    const period = logPeriod();
    const esc = s => String(s ?? '').replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
    const win = window.open('', '_blank');
    if (!win) { message.error('Popup block hai — browser me popup allow karo'); return; }
    const salRows = await fetchSalaryRows(period);
    const inr = n => '₹' + Number(n || 0).toLocaleString('en-IN');
    const salHtml = salRows ? `<h2>Salary Hisab — ${esc(period.label)}</h2>
      <table><tr><th>Staff</th><th>Monthly</th><th>Per Day</th><th>P / HD / A</th><th>Absent Ded.</th><th>Half Day Ded.</th><th>Pre-Join Ded.</th><th>Total Ded.</th><th>Net Salary</th><th>Advance (this month + previous)</th><th>Net Payable</th><th>Advance → next month</th><th>Payment</th></tr>
      ${salRows.map(r => `<tr><td>${esc(r.full_name)}</td><td>${inr(r.monthly_salary)}</td><td>${inr(r.per_day_salary)}</td><td>${r.present} / ${r.half_day} / ${r.absent}</td><td>${inr(r.absent_deduction)}</td><td>${inr(r.half_day_deduction)}</td><td>${inr(r.pre_join_deduction)}</td><td>${inr(r.total_deduction)}</td><td><b>${inr(r.net_salary)}</b></td><td>${inr(r.advance_amount)}${Number(r.advance_carry_in) > 0 ? ' + ' + inr(r.advance_carry_in) : ''}</td><td><b>${inr(r.net_payable)}</b></td><td style="color:#dc2626">${Number(r.advance_carry_out) > 0 ? inr(r.advance_carry_out) : '—'}</td><td style="color:${r.salary_status === 'paid' ? '#16a34a' : '#dc2626'};font-weight:bold">${esc((r.salary_status || 'pending').toUpperCase())}${r.paid_date ? '<br><span style="font-weight:normal;color:#555">' + esc(r.paid_date) + (r.paid_amount != null ? ' · ' + inr(r.paid_amount) : '') + '</span>' : ''}</td></tr>`).join('')}</table>` : '';
    const color = { present: '#52c41a', absent: '#ff4d4f', half_day: '#fa8c16', leave: '#1890ff', holiday: '#722ed1' };
    const sumRows = buildSummary(attLog).map(s =>
      `<tr><td><b>${esc(s.name)}</b></td><td style="color:#52c41a"><b>${s.present}</b></td><td style="color:#fa8c16"><b>${s.half_day}</b></td><td style="color:#ff4d4f"><b>${s.absent}</b></td><td>${s.leave}</td><td>${s.holiday}</td><td>${(s.present + s.half_day * 0.5).toFixed(1)}</td></tr>`
    ).join('');
    const rows = [...attLog].sort((a, b) => a.att_date.localeCompare(b.att_date) || (a.full_name || '').localeCompare(b.full_name || '')).map(r =>
      `<tr><td>${dayjs(r.att_date).format('DD MMM YYYY (ddd)')}</td><td>${esc(r.full_name || r.username)}</td><td style="color:${color[r.status] || '#666'};font-weight:bold">${statusLabel(r.status)}</td><td>${r.check_in_time || '—'}</td><td>${r.check_out_time || '—'}</td><td style="font-size:11px;color:#666">${esc(r.notes || '')}</td></tr>`
    ).join('');
    const html = `<!DOCTYPE html><html><head><title>Attendance - ${esc(period.label)}</title>
      <style>body{font-family:Arial,sans-serif;padding:24px;max-width:1000px;margin:auto}
      .header{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:18px 26px;border-radius:12px;margin-bottom:18px;text-align:center}
      .header h1{margin:0;font-size:22px}.header p{margin:5px 0 0;opacity:.9}
      h2{color:#333;border-bottom:2px solid #667eea;padding-bottom:6px;margin-top:22px;font-size:16px}
      table{width:100%;border-collapse:collapse;margin:8px 0}td,th{border:1px solid #e8e8e8;padding:6px 10px;text-align:left;font-size:12px}th{background:#f0f5ff}
      .footer{text-align:center;color:#999;font-size:11px;margin-top:24px;border-top:1px solid #eee;padding-top:8px}
      .no-print{text-align:center;margin-bottom:14px}.no-print button{background:#667eea;color:#fff;border:none;padding:10px 22px;border-radius:8px;font-size:14px;cursor:pointer;font-weight:bold}
      @media print{body{padding:10px}.no-print{display:none}.header{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body>
      <div class="no-print"><button onclick="window.print()">🖨️ Print / Save as PDF</button></div>
      <div class="header"><h1>Staff Attendance Report</h1><p>${esc(period.label)} | Bainsla Music Private Limited</p></div>
      <h2>Staff Summary</h2>
      <table><tr><th>Staff</th><th>Present</th><th>Half Day</th><th>Absent</th><th>Leave</th><th>Holiday</th><th>Paid Days</th></tr>${sumRows}</table>
      ${salHtml}
      <h2>Day-wise Log</h2>
      <table><tr><th>Date</th><th>Staff</th><th>Status</th><th>Check-In</th><th>Check-Out</th><th>Notes</th></tr>${rows}</table>
      <div class="footer">Generated on ${new Date().toLocaleString('en-IN')} — Bainsla Music Data Dashboard | data.intubemedia.com</div></body></html>`;
    win.document.write(html);
    win.document.close();
  };

  const loadAttLog = useCallback(async (filter, range) => {
    const f = filter || logFilter;
    const r = range || logDateRange;
    setAttLogLoading(true);
    try {
      const p = logPeriod(f, r);
      const from = p.from.format('YYYY-MM-DD'), to = p.to.format('YYYY-MM-DD');
      const res = await api.get(`/staff/attendance-log?from=${from}&to=${to}`);
      setAttLog(res.data || []);
    } catch (e) { message.error('Failed to load attendance log'); }
    finally { setAttLogLoading(false); }
  }, [logFilter, logDateRange, month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOfficeLoc = useCallback(async () => {
    try {
      const res = await api.get('/settings/office-location');
      setOfficeLoc(res.data);
    } catch (e) {}
  }, []);
  const saveOfficeLoc = async () => {
    setOfficeLocSaving(true);
    try {
      await api.post('/settings/office-location', officeLoc);
      message.success('Office location saved!');
    } catch (e) { message.error('Failed to save office location'); }
    finally { setOfficeLocSaving(false); }
  };

  useEffect(() => { loadQrToken(); }, [loadQrToken]);
  useEffect(() => { loadOfficeLoc(); }, [loadOfficeLoc]);
  useEffect(() => { loadAttLog(); }, [loadAttLog]);
  useEffect(() => { loadHolidays(); }, [loadHolidays]);
  useEffect(() => { loadProfileReqs(); }, [loadProfileReqs]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/staff/hr-list?month=${month}&year=${year}`);
      setRows(res.data || []);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to load staff list');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  const loadAttDay = useCallback(async (d) => {
    setAttDayLoading(true);
    try {
      const res = await api.get(`/staff/attendance-day?date=${d.format('YYYY-MM-DD')}`);
      const list = res.data || [];
      setAttDayList(list);
      const marks = {};
      const isSunday = d.day() === 0;
      list.forEach(s => {
        if (s.status) { marks[s.user_id] = s.status; }
        else if (isSunday) { marks[s.user_id] = 'holiday'; }
      });
      setAttMarks(marks);
    } catch (err) { message.error('Failed to load attendance'); }
    finally { setAttDayLoading(false); }
  }, []);

  useEffect(() => { loadAttDay(attDate); }, [attDate, loadAttDay]);

  const saveSingleAtt = async (userId, status) => {
    try {
      await api.post('/staff/attendance', { user_id: userId, date: attDate.format('YYYY-MM-DD'), status });
    } catch (err) { message.error('Failed to save'); }
  };

  const clearSingleAtt = async (userId) => {
    try {
      await api.post('/staff/attendance-clear', { date: attDate.format('YYYY-MM-DD'), user_id: userId });
    } catch (err) { message.error('Failed to clear'); }
  };

  const setMark = (userId, status) => {
    const newStatus = attMarks[userId] === status ? undefined : status;
    setAttMarks(prev => ({ ...prev, [userId]: newStatus }));
    if (newStatus) saveSingleAtt(userId, newStatus).then(() => loadAttLog());
    else clearSingleAtt(userId).then(() => loadAttLog());
  };

  const markAllPresent = async () => {
    const entries = attDayList.map(s => ({ user_id: s.user_id, status: 'present' }));
    if (!entries.length) return;
    setAttSaving(true);
    try {
      await api.post('/staff/attendance-bulk', { date: attDate.format('YYYY-MM-DD'), entries });
      message.success('All marked Present');
      loadAttDay(attDate);
      load();
      loadAttLog();
    } catch (err) { message.error(err.response?.data?.error || 'Failed to save'); }
    finally { setAttSaving(false); }
  };

  const saveBulkAtt = async () => {
    const entries = Object.entries(attMarks)
      .filter(([, st]) => st)
      .map(([uid, st]) => ({ user_id: parseInt(uid), status: st }));
    if (!entries.length) { message.warning('No attendance marked'); return; }
    setAttSaving(true);
    try {
      const res = await api.post('/staff/attendance-bulk', { date: attDate.format('YYYY-MM-DD'), entries });
      message.success(res.data?.message || 'Saved');
      loadAttDay(attDate);
      load();
      loadAttLog();
    } catch (err) { message.error(err.response?.data?.error || 'Failed to save'); }
    finally { setAttSaving(false); }
  };

  const attStats = {
    total: attDayList.length,
    present: Object.values(attMarks).filter(s => s === 'present').length,
    absent: Object.values(attMarks).filter(s => s === 'absent').length,
    half_day: Object.values(attMarks).filter(s => s === 'half_day').length,
    leave: Object.values(attMarks).filter(s => s === 'leave').length,
    holiday: Object.values(attMarks).filter(s => s === 'holiday').length,
    not_marked: attDayList.length - Object.values(attMarks).filter(s => s).length,
  };

  const loadDetail = useCallback(async (userId) => {
    try {
      const res = await api.get(`/staff/hr/${userId}?month=${month}&year=${year}`);
      setDetail(res.data);
      // Build colour grid from existing attendance (day -> status).
      const gm = {};
      (res.data.attendance || []).forEach(a => {
        const day = parseInt(String(a.att_date).slice(8, 10), 10);
        if (day) gm[day] = a.status;
      });
      setGridMarks(gm);
      setOrigGridMarks(gm);
      const p = res.data.profile || {};
      profileForm.setFieldsValue({
        designation: p.designation || '',
        phone: p.phone || '',
        joining_date: p.joining_date ? dayjs(p.joining_date) : null,
        monthly_salary: p.monthly_salary || 0,
        status: p.status || 'active',
        notes: p.notes || '',
        personal_email: p.personal_email || '',
        phone2: p.phone2 || '',
        aadhar_number: p.aadhar_number || '',
        pan_number: p.pan_number || '',
        address: p.address || '',
      });
      const sal = (res.data.salary_history || []).find(s => +s.month === +month && +s.year === +year);
      salaryForm.setFieldsValue({
        sal_month: month,
        sal_year: year,
        amount: sal ? Number(sal.amount) : (p.monthly_salary || 0),
        mode: sal ? sal.mode : 'bank',
        status: sal ? sal.status : 'pending',
        paid_date: sal && sal.paid_date ? dayjs(sal.paid_date) : null,
        notes: sal ? sal.notes : '',
        send_mail: true,
      });
      attForm.setFieldsValue({ date: dayjs(), status: 'present', notes: '' });
      const perf = (res.data.performance_history || []).find(s => +s.month === +month && +s.year === +year);
      perfForm.setFieldsValue({ rating: perf ? perf.rating : 3, remark: perf ? perf.remark : '' });
      // Load advances
      try {
        const advRes = await api.get(`/staff/advances?user_id=${userId}`);
        setAdvances(advRes.data.advances || []);
        setAdvanceTotal(advRes.data.total_pending || 0);
      } catch (e) { setAdvances([]); setAdvanceTotal(0); }
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to load details');
    }
  }, [month, year, profileForm, salaryForm, attForm, perfForm]);

  const openManage = async (record, tab = 'profile') => {
    setActiveUser(record);
    setManageTab(tab);
    setOpen(true);
    await loadDetail(record.user_id);
  };

  const saveProfile = async (v) => {
    try {
      const fd = new FormData();
      fd.append('user_id', activeUser.user_id);
      fd.append('designation', v.designation || '');
      fd.append('phone', v.phone || '');
      fd.append('joining_date', v.joining_date ? v.joining_date.format('YYYY-MM-DD') : '');
      fd.append('monthly_salary', v.monthly_salary || 0);
      fd.append('status', v.status || 'active');
      fd.append('notes', v.notes || '');
      fd.append('personal_email', v.personal_email || '');
      fd.append('phone2', v.phone2 || '');
      fd.append('aadhar_number', v.aadhar_number || '');
      fd.append('pan_number', v.pan_number || '');
      fd.append('address', v.address || '');
      if (v.photo && v.photo.file) fd.append('photo', v.photo.file.originFileObj || v.photo.file);
      if (v.aadhar_doc && v.aadhar_doc.file) fd.append('aadhar_doc', v.aadhar_doc.file.originFileObj || v.aadhar_doc.file);
      if (v.offer_letter && v.offer_letter.file) fd.append('offer_letter', v.offer_letter.file.originFileObj || v.offer_letter.file);
      if (v.joining_letter && v.joining_letter.file) fd.append('joining_letter', v.joining_letter.file.originFileObj || v.joining_letter.file);
      await api.post('/staff/profile', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      message.success('Profile saved');
      load(); loadDetail(activeUser.user_id);
    } catch (err) { message.error(err.response?.data?.error || 'Failed to save'); }
  };

  const showSalaryMail = (mail) => {
    if (!mail) return;
    if (String(mail).startsWith('sent')) message.success(`Staff ko mail gaya (${String(mail).replace('sent to ', '')})`);
    else message.warning(`Staff ko mail nahi gaya: ${mail}`);
  };

  const quickMarkPaid = async (r) => {
    try {
      const amount = Math.round(Number(r.net_payable != null ? r.net_payable : r.net_salary || 0));
      const res = await api.post('/staff/salary', {
        user_id: r.user_id, month, year, amount, mode: 'bank', status: 'paid',
        paid_date: dayjs().format('YYYY-MM-DD'), send_mail: true,
      });
      message.success(`${r.full_name} — ${MONTHS[month - 1]} ${year} salary Paid`);
      showSalaryMail(res?.data?.mail);
      load();
    } catch (err) { message.error(err.response?.data?.error || 'Failed to save'); }
  };

  const saveSalary = async (v) => {
    let res;
    try {
      const salMonth = v.sal_month || month;
      const salYear = v.sal_year || year;
      // If a payment-proof photo is attached, send multipart; else plain JSON.
      if (salaryProof) {
        const fd = new FormData();
        fd.append('user_id', activeUser.user_id);
        fd.append('month', salMonth); fd.append('year', salYear);
        fd.append('amount', v.amount || 0); fd.append('mode', v.mode); fd.append('status', v.status);
        if (v.paid_date) fd.append('paid_date', v.paid_date.format('YYYY-MM-DD'));
        if (v.notes) fd.append('notes', v.notes);
        fd.append('send_mail', v.send_mail === false ? '0' : '1');
        fd.append('proof', salaryProof);
        res = await api.post('/staff/salary', fd);
      } else {
        res = await api.post('/staff/salary', {
          user_id: activeUser.user_id, month: salMonth, year: salYear,
          amount: v.amount || 0, mode: v.mode, status: v.status,
          paid_date: v.paid_date ? v.paid_date.format('YYYY-MM-DD') : null, notes: v.notes,
          send_mail: v.send_mail !== false,
        });
      }
      setSalaryProof(null);
      message.success('Salary saved');
      showSalaryMail(res?.data?.mail);
      load(); loadDetail(activeUser.user_id);
    } catch (err) { message.error(err.response?.data?.error || 'Failed to save'); }
  };

  const deleteSalary = async (id) => {
    try { await api.delete(`/staff/salary/${id}`); message.success('Record deleted'); loadDetail(activeUser.user_id); load(); }
    catch (err) { message.error(err.response?.data?.error || 'Failed to delete'); }
  };

  const saveAttendance = async (v) => {
    try {
      await api.post('/staff/attendance', {
        user_id: activeUser.user_id,
        date: v.date ? v.date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        status: v.status, notes: v.notes,
      });
      message.success('Attendance marked');
      load(); loadDetail(activeUser.user_id); loadAttLog();
    } catch (err) { message.error(err.response?.data?.error || 'Failed to save'); }
  };

  const saveAttendanceRange = async (v) => {
    if (!v.range || v.range.length !== 2) { message.error('Please choose a date range'); return; }
    try {
      const res = await api.post('/staff/attendance-range', {
        user_id: activeUser.user_id,
        from: v.range[0].format('YYYY-MM-DD'),
        to: v.range[1].format('YYYY-MM-DD'),
        status: v.status || 'present',
        only_weekdays: !!v.only_weekdays,
      });
      message.success(res.data?.message || 'Marked');
      attRangeForm.resetFields();
      load(); loadDetail(activeUser.user_id); loadAttLog();
    } catch (err) { message.error(err.response?.data?.error || 'Failed to save range'); }
  };

  // Cycle a day cell: (none/present) -> present -> absent -> clear
  const cycleGridDay = (day) => {
    setGridMarks(prev => {
      const cur = prev[day];
      const next = { ...prev };
      if (cur === 'present') next[day] = 'absent';
      else if (cur === 'absent') delete next[day];
      else next[day] = 'present';
      return next;
    });
  };

  const saveMonthGrid = async (fillRest) => {
    if (!activeUser) return;
    setGridSaving(true);
    // Days that had a stored status but were un-marked (cleared) → delete in DB.
    const clearDays = fillRest ? [] : Object.keys(origGridMarks)
      .filter(day => !gridMarks[day])
      .map(day => parseInt(day, 10));
    try {
      const res = await api.post('/staff/attendance-month', {
        user_id: activeUser.user_id, year, month,
        marks: gridMarks, fill_rest_present: !!fillRest, clear_days: clearDays,
      });
      message.success(res.data?.message || 'Saved');
      load(); loadDetail(activeUser.user_id); loadAttLog();
    } catch (err) { message.error(err.response?.data?.error || 'Failed to save'); }
    finally { setGridSaving(false); }
  };

  // Clear this staff's WHOLE month attendance (undo an accidental mass-mark).
  const clearMonthGrid = async () => {
    if (!activeUser) return;
    setGridSaving(true);
    try {
      const res = await api.post('/staff/attendance-month-clear', {
        user_id: activeUser.user_id, year, month,
      });
      message.success(res.data?.message || 'Cleared');
      setGridMarks({});
      load(); loadDetail(activeUser.user_id); loadAttLog();
    } catch (err) { message.error(err.response?.data?.error || 'Failed to clear'); }
    finally { setGridSaving(false); }
  };

  const importAttendance = async (fileObj) => {
    const fd = new FormData();
    fd.append('file', fileObj);
    try {
      const res = await api.post('/staff/attendance-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data || {};
      message.success(d.message || 'Imported');
      if (d.unknown_staff && d.unknown_staff.length) {
        Modal.warning({ title: 'Some staff did not match', content: 'These names were not found in the database: ' + d.unknown_staff.join(', ') });
      }
      load(); if (activeUser) loadDetail(activeUser.user_id);
    } catch (err) { message.error(err.response?.data?.error || 'Import failed'); }
    return false;
  };

  const savePerformance = async (v) => {
    try {
      await api.post('/staff/performance', {
        user_id: activeUser.user_id, month, year, rating: v.rating, remark: v.remark,
      });
      message.success('Performance saved');
      load(); loadDetail(activeUser.user_id);
    } catch (err) { message.error(err.response?.data?.error || 'Failed to save'); }
  };

  const saveAdvance = async (v) => {
    try {
      await api.post('/staff/advance', {
        user_id: activeUser.user_id, amount: v.amount, type: v.type || 'advance',
        reason: v.reason || '', given_date: v.given_date ? v.given_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        salary_month: v.salary_month ? v.salary_month.month() + 1 : null,
        salary_year: v.salary_month ? v.salary_month.year() : null,
      });
      message.success('Advance/loan recorded');
      advanceForm.resetFields();
      load(); loadDetail(activeUser.user_id);
    } catch (err) { message.error('Failed to save advance'); }
  };
  const openEditAdvance = (r) => {
    setEditingAdv(r);
    editAdvForm.setFieldsValue({
      amount: Number(r.amount), type: r.type || 'advance', reason: r.reason || '',
      given_date: r.given_date ? dayjs(r.given_date) : dayjs(),
      salary_month: r.salary_month ? dayjs(`${r.salary_year}-${String(r.salary_month).padStart(2, '0')}-01`) : null,
    });
  };
  const saveEditAdvance = async (v) => {
    if (!editingAdv) return;
    try {
      await api.put(`/staff/advance/${editingAdv.id}`, {
        amount: v.amount, type: v.type || 'advance', reason: v.reason || '',
        given_date: v.given_date ? v.given_date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        salary_month: v.salary_month ? v.salary_month.month() + 1 : null,
        salary_year: v.salary_month ? v.salary_month.year() : null,
      });
      message.success('Advance updated');
      setEditingAdv(null);
      load(); loadDetail(activeUser.user_id);
    } catch (err) { message.error(err.response?.data?.error || 'Failed to update advance'); }
  };
  const deleteAdvance = async (id) => {
    try {
      await api.delete(`/staff/advance/${id}`);
      message.success('Advance deleted');
      load(); loadDetail(activeUser.user_id);
    } catch (err) { message.error('Failed to delete'); }
  };

  // Download monthly PDF for a specific staff member
  const salaryMonth = month, salaryYear = year;
  const downloadStaffMonthlyPDF = async (userId, staffName, pdfMonth, pdfYear) => {
    const month = pdfMonth || salaryMonth, year = pdfYear || salaryYear;
    try {
      message.loading({ content: 'Loading report...', key: 'pdf' });
      const res = await api.get(`/staff/hr/${userId}?month=${month}&year=${year}`);
      const d = res.data;
      const p = d.profile || {};
      const a = d.attendance_summary || {};
      const u = d.user || {};
      const attRows = (d.attendance || []).map(r =>
        `<tr><td>${dayjs(r.att_date).format('DD MMM YYYY (ddd)')}</td><td style="color:${r.status === 'present' ? '#52c41a' : r.status === 'absent' ? '#ff4d4f' : r.status === 'holiday' ? '#722ed1' : '#faad14'};font-weight:bold">${(r.status || '').replace('_', ' ').toUpperCase()}</td><td>${r.check_in_time || '—'}</td><td>${r.check_out_time || '—'}</td><td>${r.notes || ''}</td></tr>`
      ).join('');
      const salaryRows = (d.salary_history || []).map(r =>
        `<tr><td>${MONTHS[r.month - 1]} ${r.year}</td><td>₹${Number(r.amount).toLocaleString('en-IN')}</td><td>${r.mode || '—'}</td><td style="color:${r.status === 'paid' ? '#52c41a' : '#faad14'};font-weight:bold">${(r.status || '').toUpperCase()}</td><td>${r.paid_date || '—'}</td></tr>`
      ).join('');
      const html = `<!DOCTYPE html><html><head><title>Monthly Report - ${staffName} - ${MONTHS[month-1]} ${year}</title>
      <style>body{font-family:Arial,sans-serif;padding:30px;max-width:900px;margin:auto}
      .header{background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;padding:20px 30px;border-radius:12px;margin-bottom:20px;text-align:center}
      .header h1{margin:0;font-size:22px}.header p{margin:5px 0 0;opacity:0.9}
      h2{color:#333;border-bottom:2px solid #667eea;padding-bottom:8px;margin-top:25px}
      table{width:100%;border-collapse:collapse;margin:10px 0}
      td,th{border:1px solid #e8e8e8;padding:8px 12px;text-align:left;font-size:13px}
      th{background:#f0f5ff;font-weight:bold}
      .summary-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin:15px 0}
      .summary-box{background:#f9f9f9;border-radius:8px;padding:12px;text-align:center;border:1px solid #eee}
      .summary-box .label{font-size:11px;color:#888;text-transform:uppercase}.summary-box .value{font-size:22px;font-weight:bold;margin-top:4px}
      .highlight{color:#52c41a}.deduction{color:#ff4d4f}.purple{color:#722ed1}
      .salary-card{background:#f0f5ff;border:2px solid #667eea;border-radius:12px;padding:20px;margin:15px 0}
      .net-salary{font-size:28px;font-weight:bold;color:#52c41a;text-align:center;margin:10px 0}
      .footer{text-align:center;color:#999;font-size:11px;margin-top:30px;border-top:1px solid #eee;padding-top:10px}
      .no-print{text-align:center;margin-bottom:16px}
      .no-print button{background:#667eea;color:#fff;border:none;padding:10px 22px;border-radius:8px;font-size:14px;cursor:pointer;font-weight:bold}
      @media print{body{padding:15px}.no-print{display:none}.header{-webkit-print-color-adjust:exact;print-color-adjust:exact}}</style></head><body>
      <div class="no-print"><button onclick="window.print()">🖨️ Print / Save as PDF</button></div>
      <div class="header"><h1>${staffName} — Monthly Report</h1><p>${MONTHS[month-1]} ${year} | Bainsla Music Private Limited</p></div>
      <h2>👤 Profile</h2>
      <table><tr><th>Name</th><td>${u.full_name||staffName}</td><th>Designation</th><td>${p.designation||'—'}</td></tr>
      <tr><th>Phone</th><td>${p.phone||'—'}</td><th>Email</th><td>${p.personal_email||u.email||'—'}</td></tr>
      <tr><th>Joining Date</th><td>${p.joining_date||'—'}</td><th>Status</th><td>${p.status||'active'}</td></tr></table>
      <h2>📊 Attendance Summary</h2>
      <div class="summary-grid">
        <div class="summary-box"><div class="label">Working Days</div><div class="value">${a.working_days||0}</div></div>
        <div class="summary-box"><div class="label">Present</div><div class="value highlight">${a.present||0}</div></div>
        <div class="summary-box"><div class="label">Absent</div><div class="value deduction">${a.absent||0}</div></div>
        <div class="summary-box"><div class="label">Half Day</div><div class="value" style="color:#faad14">${a.half_day||0}</div></div>
        <div class="summary-box"><div class="label">Leave</div><div class="value">${a.leave||0}</div></div>
        <div class="summary-box"><div class="label">Holiday</div><div class="value purple">${a.holiday||0}</div></div>
        <div class="summary-box"><div class="label">Days in Month</div><div class="value">${a.days_in_month||0}</div></div>
        <div class="summary-box"><div class="label">Sundays (off)</div><div class="value">${a.sundays||0}</div></div>
      </div>
      <h2>💰 Salary Breakdown</h2>
      <div class="salary-card">
        <table style="border:none"><tr><th style="background:transparent;border:none">Monthly Salary</th><td style="border:none;font-size:16px">₹${Number(a.monthly_salary||0).toLocaleString('en-IN')}</td>
        <th style="background:transparent;border:none">Per Day</th><td style="border:none;font-size:16px">₹${Number(a.per_day_salary||0).toLocaleString('en-IN')}</td></tr>
        <tr><th style="background:transparent;border:none">Absent (${Number(a.absent||0)} day)</th><td style="border:none;font-size:16px;color:#ff4d4f">- ₹${Number(a.absent_deduction ?? a.total_deduction ?? 0).toLocaleString('en-IN')}</td>
        <th style="background:transparent;border:none">Half Day (${Number(a.half_day||0)} × ½)</th><td style="border:none;font-size:16px;color:#fa8c16">- ₹${Number(a.half_day_deduction||0).toLocaleString('en-IN')}</td></tr>
        <tr>
        ${Number(a.pre_join_days||0) > 0 ? `<th style="background:transparent;border:none">Before Joining (${a.pre_join_days} day)</th><td style="border:none;font-size:16px;color:#ff4d4f">- ₹${Number(a.pre_join_deduction||0).toLocaleString('en-IN')}</td>` : '<th style="background:transparent;border:none"></th><td style="border:none"></td>'}</tr>
        <tr><th style="background:transparent;border:none">Total Deduction</th><td style="border:none;font-size:16px;color:#ff4d4f">- ₹${Number(a.total_deduction||0).toLocaleString('en-IN')}</td>
        <th style="background:transparent;border:none">Net Salary</th><td style="border:none"></td></tr></table>
        <div class="net-salary">₹${Number(a.net_salary||0).toLocaleString('en-IN')}</div>
      </div>
      <h2>📅 Daily Attendance</h2>
      <table><tr><th>Date</th><th>Status</th><th>Check-In</th><th>Check-Out</th><th>Notes</th></tr>${attRows || '<tr><td colspan="5" style="text-align:center;color:#999">No attendance records</td></tr>'}</table>
      ${salaryRows ? `<h2>💳 Salary History</h2><table><tr><th>Period</th><th>Amount</th><th>Mode</th><th>Status</th><th>Paid Date</th></tr>${salaryRows}</table>` : ''}
      <div class="footer">Generated on ${new Date().toLocaleString('en-IN')} — Bainsla Music Data Dashboard | data.intubemedia.com</div>
      </body></html>`;
      const win = window.open('', '_blank');
      win.document.write(html);
      win.document.close();
      message.success({ content: 'Report opened — click "Print / Save as PDF" if you want to download', key: 'pdf' });
    } catch (err) {
      message.error({ content: 'Failed to load report', key: 'pdf' });
    }
  };

  // Monthly Attendance Summary (real .xlsx): one row per staff — days present/absent + salary + deduction + net
  const downloadMonthlySummary = async () => {
    const m = dayjs().year(year).month(month - 1);
    setSummaryLoading(true);
    try {
      const res = await api.get('/staff/attendance-monthly', { params: { month: m.month() + 1, year: m.year() } });
      const rows = res.data?.rows || [];
      if (!rows.length) { message.warning('No staff data for this month'); return; }
      const aoa = [[
        'Staff Name', 'Designation', 'Joining Date', 'Present', 'Half Day', 'Absent', 'Leave', 'Holiday',
        'Present Days', 'Days in Month', 'Pre-Join Days',
        'Monthly Salary', 'Per Day', 'Absent Deduction', 'Pre-Join Deduction', 'Total Deduction', 'Net Salary', 'Salary Status'
      ]];
      rows.forEach(r => aoa.push([
        r.full_name, r.designation, r.joining_date || '—', r.present, r.half_day, r.absent, r.leave, r.holiday,
        r.present_days, r.days_in_month, r.pre_join_days || 0,
        r.monthly_salary, r.per_day_salary, r.absent_deduction, r.pre_join_deduction || 0, r.total_deduction, r.net_salary,
        (r.salary_status || 'pending').toUpperCase()
      ]));
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [{ wch: 22 }, { wch: 18 }, { wch: 12 }, ...Array(5).fill({ wch: 8 }), ...Array(3).fill({ wch: 11 }), ...Array(6).fill({ wch: 13 }), { wch: 13 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `${MONTHS[m.month()]} ${m.year()}`);
      XLSX.writeFile(wb, `attendance-summary-${m.format('YYYY-MM')}.xlsx`);
      message.success(`Downloaded ${MONTHS[m.month()]} ${m.year()} attendance (${rows.length} staff)`);
    } catch (e) {
      message.error('Failed to download monthly attendance');
    } finally { setSummaryLoading(false); }
  };

  // Attendance log Excel (.xlsx, all staff): Summary sheet + day-wise Log sheet with check-in/out and half-day notes
  const downloadAttendanceExcel = async () => {
    const rows = attLog || [];
    if (!rows.length) { message.warning('No attendance data to export'); return; }
    const period = logPeriod();
    const salRows = await fetchSalaryRows(period);
    const wb = XLSX.utils.book_new();
    const sum = [['Staff Name', 'Present', 'Half Day', 'Absent', 'Leave', 'Holiday', 'Paid Days']];
    buildSummary(rows).forEach(s => sum.push([s.name, s.present, s.half_day, s.absent, s.leave, s.holiday, s.present + s.half_day * 0.5]));
    const ws1 = XLSX.utils.aoa_to_sheet(sum);
    ws1['!cols'] = [{ wch: 24 }, ...Array(6).fill({ wch: 10 })];
    XLSX.utils.book_append_sheet(wb, ws1, 'Summary');
    if (salRows) {
      const ws3 = XLSX.utils.aoa_to_sheet([SALARY_HEAD, ...salRows.map(salaryRowArr)]);
      ws3['!cols'] = [{ wch: 24 }, { wch: 16 }, ...Array(SALARY_HEAD.length - 2).fill({ wch: 13 })];
      XLSX.utils.book_append_sheet(wb, ws3, 'Salary Hisab');
    }
    const log = [['Date', 'Day', 'Staff Name', 'Status', 'Check-In', 'Check-Out', 'Notes']];
    [...rows].sort((a, b) => a.att_date.localeCompare(b.att_date) || (a.full_name || '').localeCompare(b.full_name || ''))
      .forEach(r => log.push([r.att_date, dayjs(r.att_date).format('ddd'), r.full_name || r.username, statusLabel(r.status), r.check_in_time || '', r.check_out_time || '', r.notes || '']));
    const ws2 = XLSX.utils.aoa_to_sheet(log);
    ws2['!cols'] = [{ wch: 12 }, { wch: 6 }, { wch: 24 }, { wch: 11 }, { wch: 10 }, { wch: 10 }, { wch: 50 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Day-wise Log');
    XLSX.writeFile(wb, `attendance-${period.from.format('YYYY-MM-DD')}_${period.to.format('YYYY-MM-DD')}.xlsx`);
    message.success(`Excel downloaded — ${period.label}`);
  };

  const statusTag = (s) => s === 'paid'
    ? <Tag color="success">Paid</Tag>
    : <Tag color="warning">Pending</Tag>;

  const attTag = (s) => ({
    present: <Tag color="success">Present</Tag>,
    absent: <Tag color="error">Absent</Tag>,
    half_day: <Tag color="warning">Half-day</Tag>,
    not_marked: <Tag color="default">Not Marked</Tag>,
    leave: <Tag color="default">Leave</Tag>,
  }[s] || <Tag>{s}</Tag>);

  const columns = [
    { title: 'Staff', dataIndex: 'full_name', key: 'name',
      render: (t, r) => <Space direction="vertical" size={0}>
        <Text strong>{t}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>{r.designation || r.role}</Text>
      </Space> },
    { title: 'Monthly Salary', dataIndex: 'monthly_salary', key: 'sal',
      render: (v) => `₹${Number(v).toLocaleString('en-IN')}` },
    { title: `Payment (${MONTHS[month - 1]} ${year})`, key: 'pay',
      render: (_, r) => <Space onClick={e => e.stopPropagation()}>
        {r.salary_status === 'paid'
          ? <Checkbox checked disabled />
          : <Popconfirm
              title={`${r.full_name} ki ${MONTHS[month - 1]} ${year} salary daal di?`}
              description={`₹${Math.round(Number(r.net_payable != null ? r.net_payable : r.net_salary || 0)).toLocaleString('en-IN')} Paid (BANK, aaj ki date) — staff ko mail jayega`}
              okText="Haan, Paid" cancelText="Nahi" onConfirm={() => quickMarkPaid(r)}>
              <Checkbox checked={false} />
            </Popconfirm>}
        {statusTag(r.salary_status)}{r.salary_amount > 0 && <Text>₹{Number(r.salary_amount).toLocaleString('en-IN')}</Text>}
      </Space> },
    { title: 'Present', dataIndex: 'present_days', key: 'pd',
      render: (v) => <Tag color="green">{v}</Tag> },
    { title: 'Absent', dataIndex: 'absent', key: 'ab',
      render: (v, r) => (Number(v) > 0 || Number(r.pre_join_days) > 0)
        ? <Tag color="red">{v}{Number(r.pre_join_days) > 0 ? ` (+${r.pre_join_days} pre-join)` : ''}</Tag>
        : <Tag color="default">0</Tag> },
    { title: 'Advance', dataIndex: 'advance_amount', key: 'adv',
      render: (v, r) => {
        const direct = Number(v || 0), carry = Number(r.advance_carry_in || 0), total = direct + carry;
        if (total <= 0) return <Text type="secondary">—</Text>;
        return <Space direction="vertical" size={0}>
          <Text strong style={{ color: '#d4380d' }}>−₹{total.toLocaleString('en-IN')}</Text>
          {direct > 0 && carry > 0 && <Text type="secondary" style={{ fontSize: 11 }}>this month ₹{direct.toLocaleString('en-IN')} + previous ₹{Math.round(carry).toLocaleString('en-IN')}</Text>}
          {direct === 0 && carry > 0 && <Text type="secondary" style={{ fontSize: 11 }}>carried from previous month</Text>}
        </Space>;
      } },
    { title: 'Net Payable', dataIndex: 'net_payable', key: 'net',
      render: (v, r) => <Space direction="vertical" size={0}>
        <Text strong style={{ color: '#52c41a' }}>₹{Number(v != null ? v : r.net_salary || 0).toLocaleString('en-IN')}</Text>
        {Number(r.advance_carry_out) > 0 && <Tag color="red" style={{ fontSize: 11 }}>₹{Number(r.advance_carry_out).toLocaleString('en-IN')} advance → next month</Tag>}
      </Space> },
    { title: 'Action', key: 'act',
      render: (_, r) => <Space size={4} wrap onClick={e => e.stopPropagation()}>
        <Button type="primary" ghost size="small" icon={<CalendarOutlined />} onClick={() => openManage(r, 'attendance')}>Hisab</Button>
        <Button size="small" icon={<EditOutlined />} onClick={() => openManage(r, 'profile')}>Manage</Button>
        <Button size="small" icon={<FilePdfOutlined />} onClick={() => downloadStaffMonthlyPDF(r.user_id, r.full_name)}>PDF</Button>
      </Space> },
  ];

  const salaryCols = [
    { title: 'Period', key: 'p', render: (_, r) => `${MONTHS[r.month - 1]} ${r.year}` },
    { title: 'Amount', dataIndex: 'amount', render: (v) => `₹${Number(v).toLocaleString('en-IN')}` },
    { title: 'Mode', dataIndex: 'mode', render: (v) => <Tag>{v}</Tag> },
    { title: 'Status', dataIndex: 'status', render: statusTag },
    { title: 'Paid Date', dataIndex: 'paid_date', render: (v) => v || '—' },
    { title: 'Proof', key: 'proof', render: (_, r) => r.proof_path
      ? <a href={`${API_BASE}/${r.proof_path}`} target="_blank" rel="noreferrer"><PaperClipOutlined /> View</a>
      : <Text type="secondary">—</Text> },
    { title: '', key: 'd', render: (_, r) => <Popconfirm title="Delete?" onConfirm={() => deleteSalary(r.id)} okText="Yes" cancelText="No"><Button danger size="small" icon={<DeleteOutlined />} /></Popconfirm> },
  ];

  const pageTitle = view === 'attendance' ? 'Staff Attendance' : view === 'salary' ? 'Staff Salary' : 'Staff Management & Salary';
  const pageIcon = view === 'attendance' ? <CalendarOutlined /> : view === 'salary' ? <DollarOutlined /> : <TeamOutlined />;

  return (
    <div>
      <Space style={{ marginBottom: 16, justifyContent: 'space-between', width: '100%' }} wrap>
        <Title level={3} style={{ margin: 0 }}>{pageIcon} {pageTitle}</Title>
        <Space>
          <Select value={month} onChange={setMonth} style={{ width: 110 }}
            options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} />
          <Select value={year} onChange={setYear} style={{ width: 100 }}
            options={YEARS.map(y => ({ value: y, label: y }))} />
        </Space>
      </Space>

      {/* Daily Attendance Section — only show in attendance or all view */}
      {(view === 'attendance' || view === 'all') && (<>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #1890ff' }}><Statistic title={<span><TeamOutlined /> Total Employees</span>} value={attStats.total} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #52c41a' }}><Statistic title={<span><CheckCircleOutlined style={{ color: '#52c41a' }} /> Present Today</span>} value={attStats.present} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #ff4d4f' }}><Statistic title={<span><CloseCircleOutlined style={{ color: '#ff4d4f' }} /> Absent Today</span>} value={attStats.absent} valueStyle={{ color: '#ff4d4f' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #faad14' }}><Statistic title={<span><ClockCircleOutlined style={{ color: '#faad14' }} /> Not Marked</span>} value={attStats.not_marked} valueStyle={{ color: '#faad14' }} /></Card></Col>
      </Row>

      <Card style={{ borderRadius: 12, marginBottom: 16 }}
        title={<Space><CalendarOutlined /> <span>Mark Attendance — {attDate.format('dddd, D MMMM YYYY')}{attDate.day() === 0 && <Tag color="purple" style={{ marginLeft: 8 }}>SUNDAY — Auto Holiday</Tag>}{holidays.find(h => h.holiday_date === attDate.format('YYYY-MM-DD')) && <Tag color="purple" style={{ marginLeft: 8 }}>HOLIDAY — {holidays.find(h => h.holiday_date === attDate.format('YYYY-MM-DD')).title}</Tag>}</span></Space>}
        extra={<Space wrap>
          <DatePicker value={attDate} onChange={d => d && setAttDate(d)} allowClear={false} />
          <Button style={{ background: '#52c41a', borderColor: '#52c41a', color: '#fff' }} icon={<CheckCircleOutlined />} loading={attSaving} onClick={markAllPresent}>All Present</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={attSaving} onClick={saveBulkAtt}>Save All</Button>
        </Space>}
        loading={attDayLoading}
      >
        {attDayList.map(s => {
          const cur = attMarks[s.user_id];
          const initials = (s.full_name || '?').charAt(0).toUpperCase();
          const statuses = [
            { key: 'present', label: 'Present', color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f' },
            { key: 'absent', label: 'Absent', color: '#ff4d4f', bg: '#fff2f0', border: '#ffa39e' },
            { key: 'half_day', label: 'Half Day', color: '#faad14', bg: '#fffbe6', border: '#ffe58f' },
            { key: 'leave', label: 'Leave', color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff' },
            { key: 'holiday', label: 'Holiday', color: '#722ed1', bg: '#f9f0ff', border: '#d3adf7' },
          ];
          return (
            <div key={s.user_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', gap: 8 }}>
              <Space>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1890ff', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: 16 }}>{initials}</div>
                <div>
                  <Text strong>{s.full_name}</Text>
                  <div><Text type="secondary" style={{ fontSize: 12 }}>{s.role}</Text></div>
                </div>
              </Space>
              <Space wrap size={4}>
                {statuses.map(st => (
                  <Button key={st.key} size="small"
                    style={cur === st.key
                      ? { background: st.bg, borderColor: st.color, color: st.color, fontWeight: 600 }
                      : { color: '#8c8c8c' }}
                    onClick={() => setMark(s.user_id, st.key)}
                  >{st.label}</Button>
                ))}
              </Space>
            </div>
          );
        })}
        {!attDayList.length && <Empty description="No staff found" />}
      </Card>

      <Card size="small" style={{ borderRadius: 12, marginBottom: 12, background: '#fafafa' }}
        title={<span><CalendarOutlined /> Attendance Excel import (bulk)</span>}>
        <Space wrap>
          <Upload showUploadList={false} accept=".csv,.xls,.xlsx" beforeUpload={importAttendance}>
            <Button icon={<UploadOutlined />}>Upload Excel/CSV</Button>
          </Upload>
          <Text type="secondary">
            Columns: <b>staff</b> (name/username/email/staff_id), <b>date</b> (YYYY-MM-DD), <b>status</b> (present/absent/half_day/leave).
          </Text>
        </Space>
      </Card>

      {/* QR Code Section */}
      <Card size="small" style={{ borderRadius: 12, marginBottom: 12, background: '#f0f5ff', borderLeft: '4px solid #1890ff' }}
        title={<span><QrcodeOutlined /> Office QR Code — Staff Attendance</span>}>
        {qrToken ? (
          <Space direction="vertical" align="center" style={{ width: '100%' }}>
            <div id="office-qr-code" style={{ background: '#fff', padding: 16, borderRadius: 12, display: 'inline-block' }}>
              <QRCodeCanvas value={`https://data.intubemedia.com/qr-scan?token=${qrToken}`} size={200} level="H"
                imageSettings={{ src: '/logo192.png', height: 36, width: 36, excavate: true }} />
            </div>
            <Text type="secondary">Staff apne phone se ye QR scan karke attendance lagayenge</Text>
            <Space>
              <Button type="primary" icon={<DownloadOutlined />} onClick={downloadQr}>Download QR (Print)</Button>
              {qrSheetUrl && <Button onClick={() => setQrSheetOpen(true)}>QR sheet dobara kholo</Button>}
              <Popconfirm title="Generate new QR? Old one will stop working." onConfirm={generateQr} okText="Yes" cancelText="No">
                <Button icon={<QrcodeOutlined />}>Regenerate</Button>
              </Popconfirm>
            </Space>
          </Space>
        ) : (
          <Space direction="vertical" align="center" style={{ width: '100%' }}>
            <Text>No QR code generated yet. Generate one to print and place in office.</Text>
            <Button type="primary" icon={<QrcodeOutlined />} loading={qrLoading} onClick={generateQr}>Generate Office QR Code</Button>
          </Space>
        )}
      </Card>

      <Modal open={qrSheetOpen} onCancel={() => setQrSheetOpen(false)} footer={null} title="Attendance QR sheet" width={420} centered>
        {qrSheetUrl && (
          <Space direction="vertical" align="center" style={{ width: '100%' }}>
            <img src={qrSheetUrl} alt="Attendance QR sheet" style={{ width: '100%', border: '1px solid #eee', borderRadius: 8 }} />
            <Space wrap>
              <a href={qrSheetUrl} download="bainsla-music-attendance-qr.png">
                <Button type="primary" icon={<DownloadOutlined />}>PNG download karo</Button>
              </a>
              <Button icon={<FilePdfOutlined />} onClick={printQrSheet}>Print</Button>
            </Space>
            <Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
              Phone par download na ho to photo ko der tak dabaye rakho → "Save image".
            </Text>
          </Space>
        )}
      </Modal>

      {/* Office Location — GPS Verification */}
      <Card size="small" style={{ borderRadius: 12, marginBottom: 12, background: '#f6ffed', borderLeft: '4px solid #52c41a' }}
        title={<span><EnvironmentOutlined /> Office Location — GPS Verification</span>}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Staff sirf office ke paas (set radius me) se QR scan karke attendance laga sakta hai. Ghar se scan nahi hoga.
        </Text>
        <Row gutter={12}>
          <Col xs={12} sm={8}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Latitude</Text>
            <InputNumber value={officeLoc.office_lat} style={{ width: '100%' }}
              step={0.0001} precision={6}
              onChange={v => setOfficeLoc(p => ({ ...p, office_lat: v || 0 }))} />
          </Col>
          <Col xs={12} sm={8}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Longitude</Text>
            <InputNumber value={officeLoc.office_lng} style={{ width: '100%' }}
              step={0.0001} precision={6}
              onChange={v => setOfficeLoc(p => ({ ...p, office_lng: v || 0 }))} />
          </Col>
          <Col xs={12} sm={8}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>QR Radius (meters)</Text>
            <InputNumber value={officeLoc.office_radius} style={{ width: '100%' }}
              min={10} max={5000}
              onChange={v => setOfficeLoc(p => ({ ...p, office_radius: v || 100 }))} />
          </Col>
          <Col xs={12} sm={8}>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>Login Radius (meters)</Text>
            <InputNumber value={officeLoc.login_radius} style={{ width: '100%' }}
              min={50} max={20000}
              onChange={v => setOfficeLoc(p => ({ ...p, login_radius: v || 500 }))} />
            <Text type="secondary" style={{ fontSize: 11 }}>Staff isi radius (default 500m) ke andar hi dashboard khol sakta hai.</Text>
          </Col>
        </Row>
        <div style={{ marginTop: 12 }}>
          <Button type="primary" icon={<SaveOutlined />} loading={officeLocSaving} onClick={saveOfficeLoc}>
            Save Location
          </Button>
          <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>
            Google Maps se coordinates copy karo (long press → coordinates copy).
            {officeLoc.office_lat !== 0 && officeLoc.office_lng !== 0 && (
              <span> Current: <a href={`https://maps.google.com/?q=${officeLoc.office_lat},${officeLoc.office_lng}`} target="_blank" rel="noreferrer">View on Map</a></span>
            )}
          </Text>
        </div>
      </Card>

      {/* Custom Holidays Management */}
      <Card size="small" style={{ borderRadius: 12, marginBottom: 12, background: '#f9f0ff', borderLeft: '4px solid #722ed1' }}
        title={<span><CalendarOutlined style={{ color: '#722ed1' }} /> Custom Holidays — {year}</span>}
        extra={<Space>
          <Button type="primary" icon={<PlusOutlined />} size="small" onClick={() => setHolidayModal(true)} style={{ background: '#722ed1', borderColor: '#722ed1' }}>Add Holiday</Button>
        </Space>}
        loading={holidaysLoading}
      >
        {holidays.length === 0 ? (
          <Text type="secondary">No custom holidays added for {year}. Sundays are auto-holiday.</Text>
        ) : (
          <Table
            dataSource={holidays}
            columns={[
              { title: '#', render: (_, __, i) => i + 1, width: 40 },
              { title: 'Date', dataIndex: 'holiday_date', render: d => {
                const dt = new Date(d);
                const day = dt.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
                const isPast = dt < new Date(new Date().setHours(0,0,0,0));
                return <span style={{ color: isPast ? '#999' : '#722ed1', fontWeight: isPast ? 'normal' : 'bold' }}>{day}</span>;
              }},
              { title: 'Holiday', dataIndex: 'title', render: t => <Tag color="purple">{t}</Tag> },
              { title: 'Added By', dataIndex: 'creator_name', render: n => n || '-' },
              { title: 'Action', width: 80, render: (_, r) => (
                <Popconfirm title="Delete this holiday?" onConfirm={() => deleteHoliday(r.id)}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              )}
            ]}
            rowKey="id"
            size="small"
            pagination={false}
          />
        )}

        <Modal title="Add Custom Holiday" open={holidayModal} onCancel={() => setHolidayModal(false)} footer={null}>
          <Form form={holidayForm} onFinish={addHoliday} layout="vertical">
            <Form.Item name="holiday_date" label="Holiday Date" rules={[{ required: true, message: 'Select date' }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
            <Form.Item name="title" label="Holiday Name (e.g. Diwali, Holi, Eid)" rules={[{ required: true, message: 'Enter holiday name' }]}>
              <Input placeholder="e.g. Independence Day" />
            </Form.Item>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Holiday add karte hi sab staff ki attendance automatic "Holiday" mark ho jayegi. Salary se paise nahi katenge.
            </Text>
            <Button type="primary" htmlType="submit" block style={{ background: '#722ed1', borderColor: '#722ed1' }}>Add Holiday</Button>
          </Form>
        </Modal>
      </Card>

      {/* Upcoming Holidays for Staff View */}
      {holidays.filter(h => new Date(h.holiday_date) >= new Date(new Date().setHours(0,0,0,0))).length > 0 && (
        <Card size="small" style={{ borderRadius: 12, marginBottom: 12, background: '#fffbe6', borderLeft: '4px solid #faad14' }}
          title={<span><CalendarOutlined style={{ color: '#faad14' }} /> Upcoming Holidays</span>}>
          <Space wrap>
            {holidays.filter(h => new Date(h.holiday_date) >= new Date(new Date().setHours(0,0,0,0))).map(h => (
              <Tag key={h.id} color="purple" style={{ fontSize: 13, padding: '4px 12px' }}>
                {new Date(h.holiday_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} — {h.title}
              </Tag>
            ))}
          </Space>
        </Card>
      )}

      {/* Attendance Log — Professional HR style */}
      <Card size="small" style={{ borderRadius: 12, marginBottom: 12 }}
        title={<span><ClockCircleOutlined /> Attendance Management</span>}
        extra={<Space wrap><Button icon={<FileExcelOutlined />} onClick={downloadExcel} size="small" type="primary" ghost>Daily Grid (Excel)</Button><Text type="secondary" style={{ fontSize: 12 }}>Month-wise salary Excel → Staff Salary page</Text></Space>}>

        {/* Filter bar */}
        <Space wrap style={{ marginBottom: 10 }}>
          <Radio.Group value={logFilter} onChange={e => { setLogFilter(e.target.value); loadAttLog(e.target.value); }} buttonStyle="solid" size="small">
            <Radio.Button value="today">Today</Radio.Button>
            <Radio.Button value="7d">7 Days</Radio.Button>
            <Radio.Button value="30d">30 Days</Radio.Button>
            <Radio.Button value="month">Monthly ({MONTHS[month - 1].slice(0, 3)} {year})</Radio.Button>
            <Radio.Button value="custom">Custom</Radio.Button>
          </Radio.Group>
          {logFilter === 'custom' && (
            <DatePicker.RangePicker size="small" value={logDateRange}
              onChange={v => { if (v) { setLogDateRange(v); loadAttLog('custom', v); } }} />
          )}
        </Space>
        <div style={{ background: '#f9f0ff', border: '1px solid #d3adf7', borderRadius: 10, padding: '8px 12px', marginBottom: 14 }}>
          <Space wrap align="center">
            <Text strong style={{ color: '#722ed1' }}><CalendarOutlined /> Pichhle mahine:</Text>
            {lastMonths.map(m => {
              const key = `m:${m.format('YYYY-MM')}`;
              return (
                <Button key={key} size="small" type={logFilter === key ? 'primary' : 'default'}
                  style={logFilter === key ? { background: '#722ed1', borderColor: '#722ed1' } : {}}
                  onClick={() => { setLogFilter(key); loadAttLog(key); }}>{m.format('MMMM YYYY')}</Button>
              );
            })}
          </Space>
          <Divider style={{ margin: '8px 0' }} />
          <Space wrap align="center">
            <Text type="secondary" style={{ fontSize: 12 }}>Abhi dikh raha: <b>{logPeriod().label}</b> ({attLog.length} entries) — isi ka download:</Text>
            <Button size="small" type="primary" style={{ background: '#1d6f42', borderColor: '#1d6f42' }} icon={<FileExcelOutlined />} onClick={downloadAttendanceExcel} disabled={!attLog.length}>Excel (.xlsx)</Button>
            <Button size="small" type="primary" danger icon={<FilePdfOutlined />} onClick={downloadAttendancePDF} disabled={!attLog.length}>PDF</Button>
          </Space>
        </div>

        {/* Summary Cards */}
        {(() => {
          const summary = buildSummary(attLog);
          const totalPresent = summary.reduce((s, r) => s + r.present, 0);
          const totalAbsent = summary.reduce((s, r) => s + r.absent, 0);
          const uniqueStaff = summary.length;
          return (
            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, background: '#f0f5ff' }}><Statistic title="Total Staff" value={uniqueStaff} prefix={<TeamOutlined />} /></Card></Col>
              <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, background: '#f6ffed' }}><Statistic title="Present" value={totalPresent} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Card></Col>
              <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, background: '#fff2f0' }}><Statistic title="Absent" value={totalAbsent} valueStyle={{ color: '#ff4d4f' }} prefix={<CloseCircleOutlined />} /></Card></Col>
              <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, background: '#fffbe6' }}><Statistic title="Attendance %" value={totalPresent + totalAbsent > 0 ? Math.round((totalPresent / (totalPresent + totalAbsent)) * 100) : 0} suffix="%" /></Card></Col>
            </Row>
          );
        })()}

        {/* Staff-wise Summary */}
        {buildSummary(attLog).length > 0 && (
          <Card size="small" title="Staff Summary" style={{ borderRadius: 10, marginBottom: 16 }}>
            <Table rowKey="user_id" dataSource={buildSummary(attLog)} pagination={false} size="small"
              columns={[
                { title: 'Staff', dataIndex: 'name', key: 'name', render: v => <Text strong>{v}</Text> },
                { title: 'Present', dataIndex: 'present', key: 'p', render: v => <Tag color="green">{v}</Tag> },
                { title: 'Absent', dataIndex: 'absent', key: 'a', render: v => <Tag color="red">{v}</Tag> },
                { title: 'Half Day', dataIndex: 'half_day', key: 'h', render: v => v > 0 ? <Tag color="orange">{v}</Tag> : '—' },
                { title: 'Leave', dataIndex: 'leave', key: 'l', render: v => v > 0 ? <Tag color="blue">{v}</Tag> : '—' },
                { title: 'Holiday', dataIndex: 'holiday', key: 'ho', render: v => v > 0 ? <Tag color="purple">{v}</Tag> : '—' },
                { title: 'Attendance %', key: 'pct', render: (_, r) => {
                  const working = r.present + r.absent + r.half_day;
                  const pct = working > 0 ? Math.round(((r.present + r.half_day * 0.5) / working) * 100) : 0;
                  return <Progress percent={pct} size="small" status={pct >= 80 ? 'success' : pct >= 50 ? 'normal' : 'exception'} />;
                }},
                { title: 'PDF', key: 'pdf', width: 80, render: (_, r) => {
                  const p = logPeriod();
                  return <Button size="small" type="primary" icon={<FilePdfOutlined />} onClick={() => downloadStaffMonthlyPDF(r.user_id, r.name, p.month || p.from.month() + 1, p.year || p.from.year())}>PDF</Button>;
                }},
              ]}
            />
          </Card>
        )}

        {/* Detailed attendance table */}
        <Table rowKey={(r, i) => `${r.user_id}-${r.att_date}-${i}`} dataSource={attLog}
          loading={attLogLoading} pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }} size="small"
          columns={[
            { title: 'Date', dataIndex: 'att_date', key: 'date', render: v => dayjs(v).format('DD MMM YYYY (ddd)'), sorter: (a, b) => a.att_date.localeCompare(b.att_date) },
            { title: 'Staff', dataIndex: 'full_name', key: 'name', render: (t, r) => <Text strong>{t || r.username}</Text> },
            { title: 'Status', dataIndex: 'status', key: 'st', render: s => {
              const colors = { present: 'green', absent: 'red', half_day: 'orange', leave: 'blue', holiday: 'purple', not_marked: 'default' };
              return <Tag color={colors[s] || 'default'}>{(s || '').replace('_', ' ').toUpperCase()}</Tag>;
            }},
            { title: 'Check-In', dataIndex: 'check_in_time', key: 'in', render: v => v ? <Tag color="green"><LoginOutlined /> {v}</Tag> : <Text type="secondary">—</Text> },
            { title: 'Check-Out', dataIndex: 'check_out_time', key: 'out', render: v => v ? <Tag color="red"><LogoutOutlined /> {v}</Tag> : <Text type="secondary">—</Text> },
            { title: 'Notes', dataIndex: 'notes', key: 'notes', render: v => v ? <Text type={/half day/i.test(v) ? 'warning' : 'secondary'} style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text> },
          ]}
        />
      </Card>
      </>)}

      {/* Staff-submitted profile change requests — admin approve karta hai */}
      {profileReqs.length > 0 && (
        <Card size="small" style={{ borderRadius: 12, marginBottom: 16, background: '#fffbe6', borderLeft: '4px solid #faad14' }}
          loading={profileReqsLoading}
          title={<span><IdcardOutlined /> Staff Profile Change Requests <Tag color="orange">{profileReqs.length} pending</Tag></span>}
          extra={<Button size="small" onClick={loadProfileReqs}>Refresh</Button>}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Staff ne apni details bhari/sudhaari hai. Jo field approve karni ho uspar tick rakho, phir Approve dabao. Salary staff kabhi nahi badal sakta.
          </Text>
          {profileReqs.map(r => {
            const fields = Object.keys(r.changes || {});
            const selected = reqFields[r.id] || [];
            return (
              <Card key={r.id} size="small" style={{ marginBottom: 12, borderRadius: 10 }}
                title={<span><UserOutlined /> {r.full_name || r.username} <Text type="secondary" style={{ fontSize: 12 }}>({r.role})</Text></span>}
                extra={<Text type="secondary" style={{ fontSize: 12 }}>{dayjs(r.created_at).format('DD MMM YYYY, hh:mm A')}</Text>}>
                <Table rowKey="field" size="small" pagination={false}
                  dataSource={fields.map(f => ({ field: f, old: r.changes[f].old, next: r.changes[f].new }))}
                  columns={[
                    { title: 'Approve', width: 80, render: (_, row) => (
                      <Checkbox checked={selected.includes(row.field)}
                        onChange={e => setReqFields(p => ({
                          ...p,
                          [r.id]: e.target.checked ? [...(p[r.id] || []), row.field] : (p[r.id] || []).filter(x => x !== row.field),
                        }))} />
                    )},
                    { title: 'Field', dataIndex: 'field', render: v => <Text strong>{v.replace(/_/g, ' ')}</Text> },
                    { title: 'Purani value', dataIndex: 'old', render: v => v
                      ? (String(v).startsWith('/uploads/') ? <a href={API_BASE + v} target="_blank" rel="noreferrer">file dekho</a> : String(v))
                      : <Text type="secondary">—</Text> },
                    { title: 'Nayi value (staff ne bheji)', dataIndex: 'next', render: v => String(v).startsWith('/uploads/')
                      ? <a href={API_BASE + v} target="_blank" rel="noreferrer">nayi file dekho</a>
                      : <Text strong style={{ color: '#52c41a' }}>{String(v)}</Text> },
                  ]} />
                {r.staff_note && <div style={{ marginTop: 8 }}><Text type="secondary">Staff note: {r.staff_note}</Text></div>}
                <Space style={{ marginTop: 12 }}>
                  <Popconfirm title="Selected fields staff ki profile me apply kar dein?" onConfirm={() => reviewProfileReq(r.id, 'approved')}>
                    <Button type="primary" icon={<CheckCircleOutlined />} disabled={!selected.length}>Approve</Button>
                  </Popconfirm>
                  <Popconfirm title="Request reject kar dein? Purani details waisi hi rahengi." onConfirm={() => reviewProfileReq(r.id, 'rejected')}>
                    <Button danger icon={<CloseCircleOutlined />}>Reject</Button>
                  </Popconfirm>
                </Space>
              </Card>
            );
          })}
        </Card>
      )}

      {/* Staff Salary Table — only show in salary or all view */}
      {(view === 'salary' || view === 'all') && (() => {
        const totalPayroll = rows.reduce((s, r) => s + Number(r.monthly_salary || 0), 0);
        const totalNet = rows.reduce((s, r) => s + Number(r.net_payable != null ? r.net_payable : r.net_salary || 0), 0);
        const totalAdvance = rows.reduce((s, r) => s + Number(r.advance_amount || 0), 0);
        const paidCount = rows.filter(r => r.salary_status === 'paid').length;
        const pendingCount = rows.length - paidCount;
        const filtered = salarySearch
          ? rows.filter(r => (r.full_name || '').toLowerCase().includes(salarySearch.toLowerCase()) || (r.designation || '').toLowerCase().includes(salarySearch.toLowerCase()))
          : rows;
        return (<>
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Salary is paid on the 10th of every month." />
          <Row gutter={12} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, background: '#f0f5ff' }}><Statistic title={<span><TeamOutlined /> Total Staff</span>} value={rows.length} /></Card></Col>
            <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, background: '#f9f0ff' }}><Statistic title="Total Monthly Payroll" value={totalPayroll} prefix="₹" precision={0} valueStyle={{ color: '#722ed1' }} /></Card></Col>
            <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, background: '#f6ffed' }}><Statistic title={`Net Payable (${MONTHS[month - 1]})`} value={totalNet} prefix="₹" precision={0} valueStyle={{ color: '#52c41a', fontWeight: 'bold' }} /></Card></Col>
            <Col xs={12} sm={6}><Card size="small" style={{ borderRadius: 10, background: '#fffbe6' }}><Statistic title="Paid / Pending" value={paidCount} suffix={`/ ${pendingCount}`} valueStyle={{ color: paidCount === rows.length && rows.length > 0 ? '#52c41a' : '#faad14' }} /></Card></Col>
          </Row>
          {(() => {
            const paidRows = rows.filter(r => r.salary_status === 'paid');
            const pendingRows = rows.filter(r => r.salary_status !== 'paid' && Number(r.monthly_salary || 0) > 0);
            const paidTotal = paidRows.reduce((s, r) => s + Number(r.salary_amount || 0), 0);
            return (
              <Card size="small" style={{ borderRadius: 12, marginBottom: 16 }}
                title={<span><CheckCircleOutlined style={{ color: '#52c41a' }} /> Salary Hisab — {MONTHS[month - 1]} {year}</span>}
                extra={<Text>Di: <Text strong style={{ color: '#52c41a' }}>{paidRows.length}</Text> · Baaki: <Text strong style={{ color: '#fa541c' }}>{pendingRows.length}</Text> · Total Paid: <Text strong>₹{paidTotal.toLocaleString('en-IN')}</Text></Text>}>
                {paidRows.length > 0 ? (
                  <Table size="small" rowKey="user_id" pagination={false} dataSource={paidRows}
                    columns={[
                      { title: 'Staff', dataIndex: 'full_name' },
                      { title: 'Amount', dataIndex: 'salary_amount', render: v => <Text strong style={{ color: '#52c41a' }}>₹{Number(v || 0).toLocaleString('en-IN')}</Text> },
                      { title: 'Date', dataIndex: 'salary_paid_date', render: v => v ? dayjs(v).format('DD-MMM-YYYY') : '—' },
                      { title: 'Mode', dataIndex: 'salary_mode', render: v => v ? String(v).toUpperCase() : '—' },
                      { title: 'Kisne di', dataIndex: 'salary_paid_by', render: v => v || '—' },
                      { title: 'Staff Mail', dataIndex: 'salary_mail_status', render: v => !v ? <Text type="secondary">—</Text> : (String(v).includes('sent to') ? <Tag color="success">Gaya</Tag> : <Tag color="error" title={v}>Nahi gaya</Tag>) },
                    ]} />
                ) : <Text type="secondary">Is mahine abhi kisi ki salary nahi daali.</Text>}
                {pendingRows.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <Text type="secondary">Baaki: </Text>
                    {pendingRows.map(r => <Tag key={r.user_id} color="orange" style={{ marginBottom: 4 }}>{r.full_name}</Tag>)}
                  </div>
                )}
              </Card>
            );
          })()}
          <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}
            title={<span><DollarOutlined /> Staff Salary — {MONTHS[month - 1]} {year}</span>}
            extra={<Space wrap>
              <Input.Search allowClear placeholder="Staff naam / designation search karo..." size="large" style={{ width: 360, maxWidth: '100%' }} value={salarySearch} onChange={e => setSalarySearch(e.target.value)} />
              <DatePicker picker="month" size="small" value={dayjs().year(year).month(month - 1)} onChange={v => { if (v) { setMonth(v.month() + 1); setYear(v.year()); } }} allowClear={false} style={{ width: 130 }} />
              <Button icon={<FileExcelOutlined />} onClick={downloadMonthlySummary} loading={summaryLoading} size="small" type="primary">Month Salary (Excel)</Button>
            </Space>}>
            <Table rowKey="user_id" loading={loading} columns={columns} dataSource={filtered} pagination={false}
              onRow={(r) => ({ onClick: () => openManage(r, 'attendance'), style: { cursor: 'pointer' } })}
              summary={() => filtered.length ? (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>₹{filtered.reduce((s, r) => s + Number(r.monthly_salary || 0), 0).toLocaleString('en-IN')}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                    <Table.Summary.Cell index={3} />
                    <Table.Summary.Cell index={4} />
                    <Table.Summary.Cell index={5}>{totalAdvance > 0 ? <Text strong style={{ color: '#fa8c16' }}>−₹{totalAdvance.toLocaleString('en-IN')}</Text> : null}</Table.Summary.Cell>
                    <Table.Summary.Cell index={6}><Text strong style={{ color: '#52c41a' }}>₹{filtered.reduce((s, r) => s + Number(r.net_payable != null ? r.net_payable : r.net_salary || 0), 0).toLocaleString('en-IN')}</Text></Table.Summary.Cell>
                    <Table.Summary.Cell index={7} />
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null}
            />
          </Card>
        </>);
      })()}

      <Modal
        title={activeUser ? `${activeUser.full_name} — ${MONTHS[month - 1]} ${year}` : ''}
        open={open} onCancel={() => setOpen(false)} footer={null} width={760} destroyOnClose
      >
        {detail && (
          <>
          <Button icon={<FilePdfOutlined />} style={{ marginBottom: 12 }} onClick={() => {
            const p = detail.profile || {};
            const a = detail.attendance_summary || {};
            const u = detail.user || {};
            const html = `<!DOCTYPE html><html><head><title>Staff Report - ${u.full_name} - ${MONTHS[month-1]} ${year}</title>
            <style>body{font-family:Arial,sans-serif;padding:30px;max-width:800px;margin:auto}
            h1{text-align:center;color:#1890ff;border-bottom:2px solid #1890ff;padding-bottom:10px}
            h2{color:#333;border-bottom:1px solid #eee;padding-bottom:5px;margin-top:20px}
            table{width:100%;border-collapse:collapse;margin:10px 0}
            td,th{border:1px solid #ddd;padding:8px;text-align:left}
            th{background:#f5f5f5;font-weight:bold}
            .highlight{font-size:18px;font-weight:bold;color:#52c41a}
            .deduction{color:#ff4d4f}
            .no-print{text-align:center;margin-bottom:16px}
            .no-print button{background:#1890ff;color:#fff;border:none;padding:10px 22px;border-radius:8px;font-size:14px;cursor:pointer;font-weight:bold}
            @media print{body{padding:20px}.no-print{display:none}}</style></head><body>
            <div class="no-print"><button onclick="window.print()">🖨️ Print / Save as PDF</button></div>
            <h1>Staff Monthly Report</h1>
            <p style="text-align:center;color:#666">${MONTHS[month-1]} ${year} — Bainsla Music</p>
            <h2>Profile</h2>
            <table><tr><th>Name</th><td>${u.full_name||''}</td><th>Designation</th><td>${p.designation||'—'}</td></tr>
            <tr><th>Phone</th><td>${p.phone||'—'}</td><th>Personal Email</th><td>${p.personal_email||'—'}</td></tr>
            <tr><th>Phone 2</th><td>${p.phone2||'—'}</td><th>Aadhar</th><td>${p.aadhar_number||'—'}</td></tr>
            <tr><th>Address</th><td colspan="3">${p.address||'—'}</td></tr>
            <tr><th>Joining Date</th><td>${p.joining_date||'—'}</td><th>Status</th><td>${p.status||'active'}</td></tr></table>
            <h2>Attendance</h2>
            <table><tr><th>Working Days</th><td>${a.working_days||'—'}</td><th>Present</th><td>${a.present||0}</td></tr>
            <tr><th>Absent</th><td>${a.absent||0}</td><th>Half Day</th><td>${a.half_day||0}</td></tr>
            <tr><th>Leave</th><td>${a.leave||0}</td><th>Holiday</th><td>${a.holiday||0}</td></tr></table>
            <h2>Salary Breakdown</h2>
            <table><tr><th>Monthly Salary</th><td>₹${Number(a.monthly_salary||0).toLocaleString('en-IN')}</td></tr>
            <tr><th>Per Day</th><td>₹${Number(a.per_day_salary||0).toLocaleString('en-IN')}</td></tr>
            <tr><th>Absent Deduction (${Number(a.absent||0)} day)</th><td class="deduction">- ₹${Number(a.absent_deduction ?? a.total_deduction ?? 0).toLocaleString('en-IN')}</td></tr>
            ${Number(a.pre_join_days||0) > 0 ? `<tr><th>Before Joining (${a.pre_join_days} day)</th><td class="deduction">- ₹${Number(a.pre_join_deduction||0).toLocaleString('en-IN')}</td></tr>` : ''}
            <tr><th>Total Deduction</th><td class="deduction">- ₹${Number(a.total_deduction||0).toLocaleString('en-IN')}</td></tr>
            <tr><th>Net Salary</th><td>₹${Number(a.net_salary||0).toLocaleString('en-IN')}</td></tr>
            ${Number(a.advance_carry_in||0) > 0 ? `<tr><th>Advance carried from previous month</th><td class="deduction">- ₹${Number(a.advance_carry_in||0).toLocaleString('en-IN')}</td></tr>` : ''}
            ${Number(a.advance_amount||0) > 0 ? `<tr><th>Advance / Payment this month (${MONTHS[month - 1]})</th><td class="deduction">- ₹${Number(a.advance_amount||0).toLocaleString('en-IN')}</td></tr>` : ''}
            <tr><th>Net Payable</th><td class="highlight">₹${Number(a.net_payable != null ? a.net_payable : a.net_salary||0).toLocaleString('en-IN')}</td></tr>
            ${Number(a.advance_carry_out||0) > 0 ? `<tr><th>Extra Advance → carried to next month</th><td class="deduction">₹${Number(a.advance_carry_out||0).toLocaleString('en-IN')}</td></tr>` : ''}</table>
            <p style="text-align:center;margin-top:30px;color:#999;font-size:12px">Generated on ${new Date().toLocaleString('en-IN')}</p>
            </body></html>`;
            const win = window.open('', '_blank');
            win.document.write(html);
            win.document.close();
          }}>Open PDF Report</Button>
          <Tabs
            activeKey={manageTab} onChange={setManageTab}
            items={[
              {
                key: 'profile', label: <span><EditOutlined /> Profile</span>,
                children: (
                  <Form form={profileForm} layout="vertical" onFinish={saveProfile}>
                    {detail?.profile?.photo && (
                      <div style={{ textAlign: 'center', marginBottom: 16 }}>
                        <Avatar size={80} src={API_BASE + detail.profile.photo} icon={<UserOutlined />} />
                      </div>
                    )}
                    <Divider orientation="left">Basic Info</Divider>
                    <Row gutter={12}>
                      <Col xs={24} sm={12}><Form.Item name="designation" label="Designation"><Input placeholder="e.g. Distribution Executive" /></Form.Item></Col>
                      <Col xs={24} sm={12}><Form.Item name="phone" label="Phone (Primary)"><Input placeholder="Mobile number" /></Form.Item></Col>
                      <Col xs={24} sm={12}><Form.Item name="phone2" label="Phone 2 (Parents/Family)"><Input placeholder="Second mobile number" /></Form.Item></Col>
                      <Col xs={24} sm={12}><Form.Item name="personal_email" label="Personal Email"><Input placeholder="Personal email (login se alag)" /></Form.Item></Col>
                      <Col xs={24} sm={12}><Form.Item name="joining_date" label="Joining Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                      <Col xs={24} sm={12}><Form.Item name="monthly_salary" label="Monthly Salary (₹)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                      <Col xs={24} sm={12}><Form.Item name="status" label="Status"><Select options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]} /></Form.Item></Col>
                      <Col xs={24} sm={12}><Form.Item name="aadhar_number" label="Aadhar Card Number"><Input placeholder="12 digit Aadhar number" maxLength={12} /></Form.Item></Col>
                      <Col xs={24} sm={12}><Form.Item name="pan_number" label="PAN Number"><Input placeholder="10 character PAN" maxLength={10} /></Form.Item></Col>
                    </Row>
                    <Form.Item name="address" label="Address"><Input.TextArea rows={2} placeholder="Permanent address" /></Form.Item>
                    <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
                    <Divider orientation="left">Documents & Photo</Divider>
                    <Row gutter={12}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="photo" label="Staff Photo" valuePropName="file">
                          <Upload beforeUpload={() => false} maxCount={1} accept="image/*">
                            <Button icon={<UploadOutlined />}>Upload Photo</Button>
                          </Upload>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="aadhar_doc" label="Aadhar Card (image/PDF)" valuePropName="file">
                          <Upload beforeUpload={() => false} maxCount={1} accept="image/*,.pdf">
                            <Button icon={<IdcardOutlined />}>Upload Aadhar</Button>
                          </Upload>
                        </Form.Item>
                        {detail?.profile?.aadhar_doc && <a href={API_BASE + detail.profile.aadhar_doc} target="_blank" rel="noreferrer"><Tag color="blue">View Aadhar</Tag></a>}
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="offer_letter" label="Offer Letter (PDF/image)" valuePropName="file">
                          <Upload beforeUpload={() => false} maxCount={1} accept="image/*,.pdf">
                            <Button icon={<FileTextOutlined />}>Upload Offer Letter</Button>
                          </Upload>
                        </Form.Item>
                        {detail?.profile?.offer_letter && <a href={API_BASE + detail.profile.offer_letter} target="_blank" rel="noreferrer" download><Tag color="green"><DownloadOutlined /> Download Offer Letter</Tag></a>}
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="joining_letter" label="Joining Letter (PDF/image)" valuePropName="file">
                          <Upload beforeUpload={() => false} maxCount={1} accept="image/*,.pdf">
                            <Button icon={<FileTextOutlined />}>Upload Joining Letter</Button>
                          </Upload>
                        </Form.Item>
                        {detail?.profile?.joining_letter && <a href={API_BASE + detail.profile.joining_letter} target="_blank" rel="noreferrer" download><Tag color="green"><DownloadOutlined /> Download Joining Letter</Tag></a>}
                      </Col>
                    </Row>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>Save Profile</Button>
                  </Form>
                ),
              },
              {
                key: 'salary', label: <span><DollarOutlined /> Salary</span>,
                children: (
                  <>
                    <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }} title="Add / Update Salary Payment">
                      <Form form={salaryForm} layout="vertical" onFinish={saveSalary}>
                        <Row gutter={12}>
                          <Col span={8}><Form.Item name="sal_month" label="Month"><Select options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} /></Form.Item></Col>
                          <Col span={8}><Form.Item name="sal_year" label="Year"><Select options={[2024, 2025, 2026].map(y => ({ value: y, label: y }))} /></Form.Item></Col>
                          <Col span={8}><Form.Item name="amount" label="Amount (₹)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                          <Col span={8}><Form.Item name="mode" label="Mode"><Select options={['cash', 'bank', 'upi', 'cheque', 'other'].map(v => ({ value: v, label: v.toUpperCase() }))} /></Form.Item></Col>
                          <Col span={8}><Form.Item name="status" label="Status"><Select options={[{ value: 'pending', label: 'Pending' }, { value: 'paid', label: 'Paid' }]} /></Form.Item></Col>
                          <Col span={8}><Form.Item name="paid_date" label="Paid Date"><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
                          <Col span={16}><Form.Item name="notes" label="Notes"><Input /></Form.Item></Col>
                          <Col span={8}><Form.Item name="send_mail" valuePropName="checked" label=" "><Checkbox>Paid par staff ko mail bhejo</Checkbox></Form.Item></Col>
                          <Col span={24}>
                            <Form.Item label="Payment Proof (screenshot/photo/PDF)">
                              <Upload
                                beforeUpload={(f) => { setSalaryProof(f); return false; }}
                                onRemove={() => setSalaryProof(null)}
                                maxCount={1}
                                fileList={salaryProof ? [{ uid: '-1', name: salaryProof.name }] : []}
                                accept="image/*,application/pdf"
                              >
                                <Button icon={<UploadOutlined />}>Select proof</Button>
                              </Upload>
                            </Form.Item>
                          </Col>
                        </Row>
                        <Button type="primary" htmlType="submit">Save Payment</Button>
                      </Form>
                    </Card>
                    <Text strong>History</Text>
                    <Table size="small" rowKey="id" columns={salaryCols} dataSource={detail.salary_history || []} pagination={false} style={{ marginTop: 8 }} locale={{ emptyText: <Empty description="No salary records" /> }} />
                  </>
                ),
              },
              {
                key: 'advances', label: <span><DollarOutlined /> Advances/Loans</span>,
                children: (
                  <>
                    <Card size="small" style={{ marginBottom: 12, background: advanceTotal > 0 ? '#fff2f0' : '#f6ffed', borderColor: advanceTotal > 0 ? '#ffccc7' : '#b7eb8f' }}>
                      <Row gutter={[12, 8]}>
                        <Col xs={12} sm={6}><Statistic title="Total Advance / Loan Given (all months)" value={advanceTotal} prefix="₹" precision={0}
                          valueStyle={{ color: advanceTotal > 0 ? '#ff4d4f' : '#52c41a', fontSize: 22, fontWeight: 'bold' }} /></Col>
                        <Col xs={12} sm={6}><Statistic title={`${MONTHS[month - 1]} ${year} Advance (total)`} value={Number(detail.attendance_summary.advance_amount || 0) + Number(detail.attendance_summary.advance_carry_in || 0)} prefix="₹" precision={0} valueStyle={{ color: '#d4380d', fontWeight: 'bold' }} /></Col>
                        <Col xs={12} sm={6}><Statistic title="Carried from previous month" value={Number(detail.attendance_summary.advance_carry_in || 0)} prefix="₹" precision={0} valueStyle={{ color: '#fa8c16' }} /></Col>
                        <Col xs={12} sm={6}><Statistic title={`${MONTHS[month - 1]} Net Payable`} value={Number(detail.attendance_summary.net_payable != null ? detail.attendance_summary.net_payable : detail.attendance_summary.net_salary || 0)} prefix="₹" precision={0} valueStyle={{ color: '#52c41a', fontWeight: 'bold' }} /></Col>
                      </Row>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                        {MONTHS[month - 1]} net salary ₹{Number(detail.attendance_summary.net_salary || 0).toLocaleString('en-IN')} − this month's advance ₹{Number(detail.attendance_summary.advance_amount || 0).toLocaleString('en-IN')} − carried from previous month ₹{Math.round(Number(detail.attendance_summary.advance_carry_in || 0)).toLocaleString('en-IN')} = Net Payable ₹{Math.round(Number(detail.attendance_summary.net_payable || 0)).toLocaleString('en-IN')}
                        {Number(detail.attendance_summary.advance_carry_out || 0) > 0 && <Text strong style={{ color: '#cf1322' }}> · ₹{Math.round(Number(detail.attendance_summary.advance_carry_out)).toLocaleString('en-IN')} advance carried to next month</Text>}
                      </Text>
                    </Card>
                    <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }} title="Add Advance / Loan">
                      <Form form={advanceForm} layout="inline" onFinish={saveAdvance} initialValues={{ type: 'advance' }}>
                        <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}><InputNumber min={1} style={{ width: 120 }} /></Form.Item>
                        <Form.Item name="type" label="Type"><Select style={{ width: 110 }} options={[{ value: 'advance', label: 'Advance' }, { value: 'loan', label: 'Loan' }]} /></Form.Item>
                        <Form.Item name="given_date" label="Payment Date"><DatePicker /></Form.Item>
                        <Form.Item name="salary_month" label="Kis mahine ki salary" tooltip="Khali chhodo to payment date ka mahina liya jayega. Jaise Aug ki salary 9 Sep ko di to yahan Aug chuno."><DatePicker picker="month" format="MMM YYYY" placeholder="Date ka mahina" style={{ width: 130 }} /></Form.Item>
                        <Form.Item name="reason" label="Reason"><Input placeholder="Reason..." style={{ width: 180 }} /></Form.Item>
                        <Button type="primary" htmlType="submit">Add</Button>
                      </Form>
                    </Card>
                    <Table size="small" rowKey="id" pagination={false}
                      columns={[
                        { title: 'Payment Date', dataIndex: 'given_date' },
                        { title: 'Salary Month', key: 'sm', render: (_, r) => <Tag color="blue">{r.salary_month ? `${MONTHS[r.salary_month - 1]} ${r.salary_year}` : dayjs(r.given_date).format('MMM YYYY')}</Tag> },
                        { title: 'Type', dataIndex: 'type', render: v => <Tag color={v === 'loan' ? 'red' : 'orange'}>{(v || '').toUpperCase()}</Tag> },
                        { title: 'Amount', dataIndex: 'amount', render: v => `₹${Number(v).toLocaleString('en-IN')}` },
                        { title: 'Reason', dataIndex: 'reason', render: v => v || '—' },
                        { title: 'Deducted', dataIndex: 'deducted', render: v => v ? <Tag color="green">Yes</Tag> : <Tag color="orange">No</Tag> },
                        { title: '', key: 'act', render: (_, r) => <Space size={4}>
                          <Button size="small" icon={<EditOutlined />} onClick={() => openEditAdvance(r)}>Edit</Button>
                          <Popconfirm title="Delete?" onConfirm={() => deleteAdvance(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
                        </Space> },
                      ]}
                      dataSource={advances}
                      locale={{ emptyText: <Empty description="No advances or loans" /> }} />
                    <Modal title="Advance / Loan edit karo" open={!!editingAdv} onCancel={() => setEditingAdv(null)} onOk={() => editAdvForm.submit()} okText="Save" destroyOnClose>
                      <Form form={editAdvForm} layout="vertical" onFinish={saveEditAdvance}>
                        <Form.Item name="amount" label="Amount (₹)" rules={[{ required: true }]}><InputNumber min={1} style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="type" label="Type"><Select options={[{ value: 'advance', label: 'Advance' }, { value: 'loan', label: 'Loan' }]} /></Form.Item>
                        <Form.Item name="given_date" label="Payment Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="salary_month" label="Kis mahine ki salary" tooltip="Khali chhodo to payment date ka mahina liya jayega"><DatePicker picker="month" format="MMM YYYY" allowClear style={{ width: '100%' }} /></Form.Item>
                        <Form.Item name="reason" label="Reason"><Input /></Form.Item>
                      </Form>
                    </Modal>
                  </>
                ),
              },
              {
                key: 'attendance', label: <span><CalendarOutlined /> Attendance</span>,
                children: (
                  <>
                    <Row gutter={12} style={{ marginBottom: 12 }}>
                      <Col xs={12} sm={6}><Statistic title="Present" value={detail.attendance_summary.present} valueStyle={{ color: '#52c41a' }} /></Col>
                      <Col xs={12} sm={6}><Statistic title="Half-day" value={detail.attendance_summary.half_day} valueStyle={{ color: '#faad14' }} /></Col>
                      <Col xs={12} sm={6}><Statistic title="Leave" value={detail.attendance_summary.leave} /></Col>
                      <Col xs={12} sm={6}><Statistic title="Absent" value={detail.attendance_summary.absent} valueStyle={{ color: '#ff4d4f' }} /></Col>
                      <Col xs={12} sm={6}><Statistic title="Holiday" value={detail.attendance_summary.holiday || 0} valueStyle={{ color: '#722ed1' }} /></Col>
                    </Row>
                    <Card size="small" style={{ marginBottom: 12, background: '#f0f5ff', borderColor: '#adc6ff' }} title="💰 Salary Breakdown (Sunday off)">
                      <Row gutter={[12, 8]}>
                        <Col xs={12} sm={6}><Statistic title="Days in Month" value={detail.attendance_summary.days_in_month} /></Col>
                        <Col xs={12} sm={6}><Statistic title="Sundays (off)" value={detail.attendance_summary.sundays} /></Col>
                        <Col xs={12} sm={6}><Statistic title="Working Days" value={detail.attendance_summary.working_days} valueStyle={{ fontWeight: 'bold' }} /></Col>
                        <Col xs={12} sm={6}><Statistic title="Per Day" value={detail.attendance_summary.per_day_salary} prefix="₹" precision={0} /></Col>
                        <Col xs={12} sm={8}><Statistic title="Monthly Salary" value={detail.attendance_summary.monthly_salary} prefix="₹" precision={0} /></Col>
                        <Col xs={12} sm={8}><Statistic title={`Total Deduction${detail.attendance_summary.pre_join_days > 0 ? ` (incl. ${detail.attendance_summary.pre_join_days}d pre-join)` : ''}`} value={detail.attendance_summary.total_deduction} prefix="- ₹" precision={0} valueStyle={{ color: '#ff4d4f' }} /></Col>
                        <Col xs={12} sm={8}><Statistic title="Net Salary" value={detail.attendance_summary.net_salary} prefix="₹" precision={0} valueStyle={{ color: '#8c8c8c', fontWeight: 'bold' }} /></Col>
                        {(Number(detail.attendance_summary.advance_amount || 0) + Number(detail.attendance_summary.advance_carry_in || 0)) > 0 && (
                          <Col xs={12} sm={8}><Statistic title={`Advance (${MONTHS[month - 1]})${Number(detail.attendance_summary.advance_carry_in || 0) > 0 ? ` — incl. ₹${Math.round(Number(detail.attendance_summary.advance_carry_in)).toLocaleString('en-IN')} from previous month` : ''}`} value={Number(detail.attendance_summary.advance_amount || 0) + Number(detail.attendance_summary.advance_carry_in || 0)} prefix="- ₹" precision={0} valueStyle={{ color: '#d4380d', fontWeight: 'bold' }} /></Col>
                        )}
                        <Col xs={12} sm={8}><Statistic title="Net Payable (advance ke baad)" value={detail.attendance_summary.net_payable != null ? detail.attendance_summary.net_payable : detail.attendance_summary.net_salary} prefix="₹" precision={0} valueStyle={{ color: '#52c41a', fontWeight: 'bold', fontSize: 20 }} /></Col>
                        {Number(detail.attendance_summary.advance_carry_out || 0) > 0 && (
                          <Col xs={12} sm={8}><Statistic title="Extra advance → carried to next month" value={detail.attendance_summary.advance_carry_out} prefix="₹" precision={0} valueStyle={{ color: '#cf1322', fontWeight: 'bold' }} /></Col>
                        )}
                      </Row>
                    </Card>
                    <Card size="small" style={{ marginBottom: 12 }}
                      title={<span><CalendarOutlined /> Quick month attendance — {MONTHS[month - 1]} {year}</span>}
                      extra={<Space wrap>
                        <Popconfirm title="Poora month clear kar de?" description={`${MONTHS[month - 1]} ${year} ki is staff ki saari marking hat jayegi, phir dobara sahi laga sakte ho.`} okText="Haan, clear" cancelText="Nahi" onConfirm={clearMonthGrid}>
                          <Button size="small" danger icon={<DeleteOutlined />} loading={gridSaving}>Clear month</Button>
                        </Popconfirm>
                        <Button size="small" loading={gridSaving} onClick={() => saveMonthGrid(false)}>Save selected</Button>
                        <Button size="small" type="primary" loading={gridSaving} onClick={() => saveMonthGrid(true)}>Save + baaki sab Present</Button>
                      </Space>}>
                      <div style={{ marginBottom: 8, color: '#888', fontSize: 12 }}>
                        Din par click karo: <Tag color="green">Present</Tag> → <Tag color="red">Absent</Tag> → clear. Sunday = <Tag color="purple">Holiday</Tag>. Galti se laga diya to us din par 2 baar click karke clear karo, phir "Save selected" dabao — DB se hat jayega. Poori galti ho gayi to <Tag color="red">Clear month</Tag> se saara month clear karke dobara laga do.
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {Array.from({ length: dayjs().year(year).month(month - 1).daysInMonth() }, (_, i) => i + 1).map(day => {
                          const isSunday = dayjs().year(year).month(month - 1).date(day).day() === 0;
                          const st = gridMarks[day];
                          let bg = '#f5f5f5', color = '#333', border = '1px solid #d9d9d9';
                          if (st === 'present') { bg = '#52c41a'; color = '#fff'; border = '1px solid #52c41a'; }
                          else if (st === 'absent') { bg = '#ff4d4f'; color = '#fff'; border = '1px solid #ff4d4f'; }
                          else if (st === 'holiday') { bg = '#f9f0ff'; color = '#722ed1'; border = '1px solid #d3adf7'; }
                          else if (isSunday) { bg = '#faf0ff'; color = '#722ed1'; border = '1px dashed #d3adf7'; }
                          return (
                            <div key={day} onClick={() => cycleGridDay(day)}
                              title={isSunday ? 'Sunday (Holiday)' : ''}
                              style={{ width: 38, height: 38, borderRadius: 8, background: bg, color, border,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', fontWeight: 600, fontSize: 13, userSelect: 'none' }}>
                              {day}
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                    <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }} title="Mark attendance">
                      <Form form={attForm} layout="inline" onFinish={saveAttendance}>
                        <Form.Item name="date" label="Date"><DatePicker /></Form.Item>
                        <Form.Item name="status" label="Status"><Select style={{ width: 130 }} options={[{ value: 'present', label: 'Present' }, { value: 'absent', label: 'Absent' }, { value: 'half_day', label: 'Half-day' }, { value: 'leave', label: 'Leave' }]} /></Form.Item>
                        <Form.Item name="notes"><Input placeholder="Notes" /></Form.Item>
                        <Button type="primary" htmlType="submit">Save</Button>
                      </Form>
                      <Divider style={{ margin: '12px 0' }}>Or — mark multiple days at once (range)</Divider>
                      <Form form={attRangeForm} layout="inline" onFinish={saveAttendanceRange} initialValues={{ status: 'present' }}>
                        <Form.Item name="range" label="From–To"><DatePicker.RangePicker /></Form.Item>
                        <Form.Item name="status"><Select style={{ width: 130 }} options={[{ value: 'present', label: 'Present' }, { value: 'absent', label: 'Absent' }, { value: 'half_day', label: 'Half-day' }, { value: 'leave', label: 'Leave' }]} /></Form.Item>
                        <Form.Item name="only_weekdays" valuePropName="checked"><Checkbox>Weekdays only (skip Sat/Sun)</Checkbox></Form.Item>
                        <Button htmlType="submit">Mark range</Button>
                      </Form>
                    </Card>
                    <Table size="small" rowKey={(r, i) => r.id || `${r.att_date}-${i}`} pagination={false}
                      columns={[
                        { title: 'Date', dataIndex: 'att_date' },
                        { title: 'Status', dataIndex: 'status', render: attTag },
                        { title: 'Notes', dataIndex: 'notes', render: (v) => v || '—' },
                      ]}
                      dataSource={detail.attendance || []}
                      locale={{ emptyText: <Empty description="No attendance this month" /> }} />
                  </>
                ),
              },
              {
                key: 'performance', label: <span><StarOutlined /> Performance</span>,
                children: (
                  <>
                    <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }} title={`${MONTHS[month - 1]} ${year} review`}>
                      <Form form={perfForm} layout="vertical" onFinish={savePerformance}>
                        <Form.Item name="rating" label="Rating (1–5)"><Rate /></Form.Item>
                        <Form.Item name="remark" label="Remark"><Input.TextArea rows={2} placeholder="How was the work..." /></Form.Item>
                        <Button type="primary" htmlType="submit">Save Review</Button>
                      </Form>
                    </Card>
                    {detail.channel_revenue && (
                      <Card size="small" style={{ marginBottom: 12 }} title="Assigned channels revenue (this month)">
                        <Statistic value={detail.channel_revenue.total} prefix="₹" />
                      </Card>
                    )}
                    <Table size="small" rowKey={(r) => `${r.year}-${r.month}`} pagination={false}
                      columns={[
                        { title: 'Period', key: 'p', render: (_, r) => `${MONTHS[r.month - 1]} ${r.year}` },
                        { title: 'Rating', dataIndex: 'rating', render: (v) => <Rate disabled value={v} style={{ fontSize: 14 }} /> },
                        { title: 'Remark', dataIndex: 'remark', render: (v) => v || '—' },
                      ]}
                      dataSource={detail.performance_history || []}
                      locale={{ emptyText: <Empty description="No reviews" /> }} />
                  </>
                ),
              },
            ]}
          />
          </>
        )}
      </Modal>
    </div>
  );
}
